// ============================================================================
// QUICK CHECK-IN MODAL (QSM-CHKIN-001)
// ============================================================================
// CONFIRMED → CHECKED_IN. Captures physical room assignments (one per booked
// room), ID document details (optional in Phase 1 per QSM-CHKIN-001; can be
// promoted to required via CheckIn:RequireIdDocumentAtCheckIn server flag per
// BR-CHKIN-001), check-in date/time, and notes.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { approvalService } from 'api/services/approvalService';
import { bookingService } from 'api/services/bookingService';
import { checkInService } from 'api/services/checkInService';
import { auditCorrectionService } from 'api/services/auditCorrectionService';
import { configService } from 'api/services/configService';
import { roomCategoryService } from 'api/services/roomCategoryService';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import SectionedModalShell, {
  ShellBlockers,
  ShellChip,
} from 'components/modals/common/SectionedModalShell';
import type { ShellBlocker, ShellSection } from 'components/modals/common/SectionedModalShell';
import CheckInGuestsPanel, {
  buildGuestsPayload,
  clearGuestRowErrors,
  createGuestRow,
  serializeGuestRows,
  toGuestRow,
  validateGuestRows,
} from './CheckInGuestsPanel';
import type { GuestRow } from './CheckInGuestsPanel';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { formatCurrency } from './booking.utils';
import ChangeSettlementModal from './ChangeSettlementModal';
import { settlementModeLabel } from 'api/types/settlement.types';
import RoomChargingModal, { canControlRoomCharging } from 'components/modals/modules/pms-module/RoomChargingModal';
import { folioService } from 'api/services/folioService';
import type { FolioDetail, FolioPostingControlRequest } from 'api/types/folio.types';
import { outputProfileService } from 'api/services/outputProfileService';
import { formCText } from 'api/types/checkin.types';
import { openPdf } from 'components/modals/modules/pms-module/IssueInvoicesModal';
import { blobErrorMessage, reserveTab, showPdf } from 'utils/reportFile';
import { mealPlanService } from 'api/services/mealPlanService';
import notificationService from 'services/notificationService';
import { paymentService } from 'api/services/paymentService';
import type { Approval } from 'api/types/approval.types';
import { requestedBasisLabel } from 'api/types/approval.types';
import type { Booking, BookingAddon, BookingDetail } from 'api/types/booking.types';
import type {
  AmendCheckInRequest, AvailableRoom, CheckIn, CheckInInventoryImpact, QuickCheckInRequest,
} from 'api/types/checkin.types';
import {
  computeShortfalls, shortfallMessage, shortNightsForCategory,
} from 'utils/checkInShortage';

/** Browser memory of the desk's "Print registration card" choice (GRC-2). */
const PRINT_CARD_KEY = 'crs.grc.printAfterCheckIn';

interface QuickCheckInModalProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  onCheckedIn: () => void;
  /**
   * 'checkin' (default) — CONFIRMED → CHECKED_IN with room assignment.
   * 'amend' — post-check-in editing of the operational record (arrival,
   * guests, occupancy, GRC) while the booking stays CHECKED_IN. Rooms are
   * locked (Room Move owns them), money is read-only, and submit goes to
   * PUT /CheckIn/{bookingId}. See CRS-CheckIn-Amend-Change-Request-Design.md.
   */
  mode?: 'checkin' | 'amend';
  /**
   * RBAC: 'room-category-change' — allows assigning a room from a DIFFERENT
   * category than booked (desk upgrade / operational room change). The server
   * reconciles inventory + line allocation; the guest keeps the booked rate.
   * Fail-closed: without the permission the picker stays category-locked.
   */
  canChangeCategory?: boolean;
  /**
   * RBAC: 'arrival-changes' — lets the desk adjust pax, meal plan and the
   * agreed rate for THIS arrival without holding booking-edit permission.
   * Fail-closed: without it the panel is hidden and the pax-mismatch warning
   * points at Reservations instead of offering a button that would 403.
   */
  canChangeArrival?: boolean;
  /**
   * RBAC: 'change-settlement' (PMS slice 3b) — shows *Change* on the settlement
   * chip. Without it the chip is read-only; the desk still sees who pays.
   */
  canChangeSettlement?: boolean;
  /** Enterprise minimize support — parent tab id. The minimize button renders only when provided. */
  tabId?: string;
  /** Display name for the minimized strip entry. */
  moduleName?: string;
  /**
   * Called when the user restores a minimized check-in from the strip —
   * the parent must re-open this modal for the given booking
   * (set its target state + open flag). Form state is restored internally.
   */
  onRestoreCheckIn?: (booking: Booking) => void;
  /**
   * Audit Corrections (2026-10-14): record a check-in missed on a date night audit has
   * closed. Needs a reason; the room list ignores today's occupancy (the server checks the
   * stays of those nights); submit goes to POST /AuditCorrections/{bookingId}/check-in,
   * which also departs a finished stay on its booked date and posts the missed nights.
   */
  auditCorrection?: boolean;
}

interface AssignmentRow {
  /** Stable key so the React list reconciles correctly when categories repeat. */
  rowKey: string;
  /** The category the picker currently offers (booked/allocated by default; changeable with permission). */
  roomCategoryId: string;
  categoryCode?: string;
  categoryName?: string;
  /** The ORIGINALLY BOOKED category — always visible so a change is never ambiguous. */
  bookedCategoryId: string;
  bookedCategoryName?: string;
  /** Currently selected room ID; '' when unassigned. */
  roomId: string;
}

const toDateInput = (d: Date): string => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
};

const toTimeInput = (d: Date): string => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(11, 16);
};

/** Option label: room number · floor · housekeeping state (dirty flagged). */
const roomLabel = (r: AvailableRoom): string => {
  const parts = [r.roomNumber];
  if (r.floorNumber) parts.push(`Floor ${r.floorNumber}`);
  if (r.housekeepingStatusName) {
    parts.push(r.housekeepingStatusCode === 'ROOM_DIRTY'
      ? `${r.housekeepingStatusName} ⚠` : r.housekeepingStatusName);
  }
  return parts.join(' · ');
};

const QuickCheckInModal: React.FC<QuickCheckInModalProps> = ({
  isOpen, booking, onClose, onCheckedIn, canChangeCategory = false,
  canChangeArrival = false,
  canChangeSettlement = false,
  mode = 'checkin',
  tabId, moduleName, onRestoreCheckIn,
  auditCorrection = false,
}) => {
  const isAmend = mode === 'amend';
  /** Audit Corrections: why the check-in was missed (kept on the card and in the audit log). */
  const [correctionReason, setCorrectionReason] = useState('');
  useEffect(() => { if (isOpen) setCorrectionReason(''); }, [isOpen, booking?.bookingId]);
  const { minimize } = useModalMinimize();
  /** The stored check-in record — amend mode's prefill + read-only rooms. */
  const [amendRecord, setAmendRecord] = useState<CheckIn | null>(null);
  /** TRUE while re-opening from the minimized strip — the on-open effects
   *  (booking reload, form reset, auto-assign) must not wipe restored state. */
  const isRestoreRef = useRef(false);
  const [detailLines, setDetailLines] = useState<BookingDetail[]>([]);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [checkInDate, setCheckInDate] = useState<string>('');
  const [checkInTime, setCheckInTime] = useState<string>('');
  // Guests panel (D3 Phase A) — one row per person on the stay. Row 0 is the
  // primary guest (pre-filled from the booking); accompanying rows are added
  // at the desk. Each row carries its own ID capture.
  const [guestRows, setGuestRows] = useState<GuestRow[]>([]);

  /**
   * The primary guest's row. Declared with the state it derives from because the
   * save callback, the blocking rule and the GRC snapshot all read it, and the
   * save callback runs well above where the blocking rule lives.
   */
  const primaryGuestRow = guestRows.find((g) => g.isPrimary);
  const guestKeyCounter = useRef(0);
  const [notes, setNotes] = useState<string>('');
  // Fallback gender aggregates (occupancy reporting) — shown ONLY while the
  // guest rows don't carry a gender for every adult; once they do, the server
  // derives the counts from the rows and these inputs disappear.
  const [numMale, setNumMale] = useState<string>('');
  const [numFemale, setNumFemale] = useState<string>('');
  // Actual pax on site (FO planning) — Adults / Children / Infants. Defaults
  // to the BOOKED pax; when the property requires all guest details the
  // counts follow the named guest rows instead (the rows are the headcount
  // then). A manual edit wins either way (paxTouchedRef). Bookings never
  // carry infants — this is the only place the desk can record them.
  const [numAdultsIn, setNumAdultsIn] = useState<string>('');
  const [numChildrenIn, setNumChildrenIn] = useState<string>('');
  const [numInfantsIn, setNumInfantsIn] = useState<string>('0');
  const paxTouchedRef = useRef(false);
  // Over-occupancy override (tables/082) — the server refuses a check-in whose
  // actual pax exceed the booked rooms' occupancy CEILING (category
  // max_occupancy) unless a reason is recorded. The input renders only while
  // the keyed counts are actually over, so normal check-ins never see it.
  const [occupancyOverrideReason, setOccupancyOverrideReason] = useState<string>('');
  // Partial arrival (BR-CHKIN-002 revised): every unassigned booked room must
  // be EXPLICITLY confirmed as a no-show — assigned + no-show = booked, no
  // ambiguous holds. Uncertain arrivals are handled manually: allocate the
  // room and keep the key ready.
  const [noShowConfirmed, setNoShowConfirmed] = useState(false);
  const [noShowRemarks, setNoShowRemarks] = useState('');
  // Booking Summary card data (decision 7): addon lines + special notes from
  // the same getFullBooking response the slot expansion already uses.
  const [bookingAddons, setBookingAddons] = useState<BookingAddon[]>([]);
  const [specialNotes, setSpecialNotes] = useState<string>('');

  // ── GRC (CRS-CheckIn-GRC-Redesign.md) ──────────────────────────────────
  const [grcNumber, setGrcNumber] = useState<string>('');
  const [grcNumberEditing, setGrcNumberEditing] = useState(false);
  const [grcAddress, setGrcAddress] = useState<string>('');
  const [grcMobile, setGrcMobile] = useState<string>('');
  const [grcEmail, setGrcEmail] = useState<string>('');
  const [purposeOfVisit, setPurposeOfVisit] = useState<string>('');
  const [purposeNote, setPurposeNote] = useState<string>('');
  const [nextDestination, setNextDestination] = useState<string>('');
  /** Config-driven: options list, default purpose, gender-split visibility. */
  const [purposeOptions, setPurposeOptions] = useState<string[]>([]);
  const [captureGenderSplit, setCaptureGenderSplit] = useState(false);
  const [requireNextDestinationSetting, setRequireNextDestination] = useState(false);
  /**
   * checkin.grc_complete_later (GRC-3): the room is allocated first and the guest
   * completes the printed card. Check-in then needs only the primary guest and the
   * mobile; ID, ID document, full guest details and Proceeding To stop blocking
   * (the server applies the same rule) and are shown as GRC pending until done.
   */
  const [grcLater, setGrcLater] = useState(false);
  /** Active nav section (sectioned shell). Guests is the landing section —
   *  verification is the desk's first real task; date/time is prefilled. */
  const [activeSection, setActiveSection] = useState<string>('guests');
  /** Collect-payment panel (decision 11). Amount is NEVER pre-filled. */
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState<string>('');
  const [payMode, setPayMode] = useState<string>('Cash');
  const [payReference, setPayReference] = useState<string>('');
  const [payPosting, setPayPosting] = useState(false);
  // Live booking record — starts as the prop, refreshed after a Modify Stay
  // edit so totals/pax/status reflect the amended booking without the parent
  // list needing to refetch first.
  const [liveBooking, setLiveBooking] = useState<Booking | null>(null);
  /** Change Settlement dialog over the check-in (PMS slice 3b). */
  const [settlementOpen, setSettlementOpen] = useState(false);
  /**
   * Room charging off (BR-FOL-008) — outlets collect directly, e.g. a group's
   * shared rooms. Amend: the stay's folio, saved at once. New check-in: the
   * folio does not exist yet, so the choice waits and is applied after commit.
   */
  const [chargingOpen, setChargingOpen] = useState(false);
  const [chargingFolio, setChargingFolio] = useState<FolioDetail | null>(null);
  const [pendingCharging, setPendingCharging] = useState<FolioPostingControlRequest | null>(null);
  /** Print the registration card right after check-in (GRC-2); the desk's choice is remembered on this browser. */
  const [printCardAfter, setPrintCardAfter] = useState<boolean>(() => {
    try { return localStorage.getItem(PRINT_CARD_KEY) !== 'false'; } catch { return true; }
  });
  /** Signed-card scan upload on Edit Check-In (GRC-4). Saved at once, outside the check-in save. */
  const [signedBusy, setSignedBusy] = useState(false);
  const signedInput = useRef<HTMLInputElement>(null);
  const uploadSignedCard = useCallback(async (file: File) => {
    if (!booking) return;
    setSignedBusy(true);
    try {
      const info = await outputProfileService.uploadSignedCard(booking.bookingId, file);
      setAmendRecord((rec) => (rec ? { ...rec, hasSignedCard: info.hasSignedCard, grcScanAt: info.uploadedAt, grcScanByName: info.uploadedByName } : rec));
      notificationService.toast.success('Signed registration card filed.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The signed card was not filed.');
    } finally {
      setSignedBusy(false);
    }
  }, [booking]);
  /** Opens the scan in a tab reserved inside the click, keeping its own type (PDF or image). */
  const viewSignedCard = useCallback(async () => {
    if (!booking) return;
    const tab = reserveTab();
    try {
      const url = URL.createObjectURL(await outputProfileService.signedCard(booking.bookingId));
      if (tab && !tab.closed) tab.location.href = url;
      else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      tab?.close();
      notificationService.toast.error(await blobErrorMessage(err, 'The signed card did not open.'));
    }
  }, [booking]);
  const changePrintCardAfter = useCallback((value: boolean) => {
    setPrintCardAfter(value);
    try { localStorage.setItem(PRINT_CARD_KEY, String(value)); } catch { /* remembered for this session only */ }
  }, []);
  /** Bumped after an arrival change to re-run the booking load effect. */
  const [reloadTick, setReloadTick] = useState(0);
  // ── FO → booking staff request lane (Phase B) ──────────────────────────
  /** Request text being composed — request-only, applies nothing to the booking. */
  const [requestText, setRequestText] = useState('');
  /** Optional: the price the desk asks for, so booking staff see the target in the Rate Worksheet. */
  const [requestAmount, setRequestAmount] = useState('');
  const [requestBasis, setRequestBasis] = useState<'PER_NIGHT' | 'STAY_TOTAL'>('STAY_TOTAL');
  const [requestSending, setRequestSending] = useState(false);
  /** This booking's FO_CHANGE_REQUEST thread (open + decided). */
  const [changeRequests, setChangeRequests] = useState<Approval[]>([]);
  /** Bumped after a submit so the thread reloads. */
  const [requestsTick, setRequestsTick] = useState(0);
  /** Arrival-changes editor visibility. */
  const [arrivalOpen, setArrivalOpen] = useState(false);
  /** Meal plans offered in the arrival-change row. */
  const [arrivalMealPlans, setArrivalMealPlans] = useState<{ id: string; planCode: string; defaultMajorMeal?: string | null }[]>([]);
  // MAP stays: which major meal the guests expect ('' = the plan's expected meal).
  const [majorMealPref, setMajorMealPref] = useState<'' | 'LUNCH' | 'DINNER' | 'EITHER'>('');
  /** Per-detail-line arrival edits, keyed by bookingDetailId. */
  const [arrivalEdits, setArrivalEdits] = useState<Record<string, {
    mealPlanId?: string; remarks: string;
  }>>({});
  const [applyingArrival, setApplyingArrival] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  /** Always-current pointer to the confirmed-save handler — lets handleSave
   *  (declared before it) commit directly on the no-warnings fast path. */
  const confirmSaveRef = useRef<() => Promise<void> | void>(() => {});
  /** Cache of available rooms per category for this booking's property. */
  const availabilityCacheRef = useRef<Record<string, AvailableRoom[]>>({});
  /** roomId → room details for every room ever loaded — powers the dirty-room warning. */
  const roomInfoRef = useRef<Record<string, AvailableRoom>>({});
  /** Snapshot of the initial form payload to detect dirty state. */
  const initialSnapshotRef = useRef<string>('');
  // ── Category change (room-category-change permission) ──
  /** All active room categories of the property, for the per-row change dropdown. */
  const [categoryList, setCategoryList] = useState<{ id: string; name: string; displayOrder?: number }[]>([]);
  /** Available-room count per category (lazy; from the same available-rooms calls). */
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  /** rowKey whose category-change dropdown is open; null = none. */
  const [changeOpenFor, setChangeOpenFor] = useState<string | null>(null);
  /** Non-booked board categories the operator has opened (lazy room fetch). */
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
  /** Board room-number filter — a large property renders hundreds of chips. */
  const [roomFilter, setRoomFilter] = useState('');
  /**
   * Room Assignment view: 'list' = one dropdown per slot (precise, shows the
   * booked-vs-changed context); 'board' = tappable free-room chips grouped by
   * category, same interaction as the Walk-In desk sheet. Same underlying
   * assignment slots either way — switching views never loses picks.
   */
  const [roomViewMode, setRoomViewMode] = useState<'list' | 'board'>('list');
  /** Free rooms per category for the board view (state so chips re-render). */
  const [boardLists, setBoardLists] = useState<Record<string, AvailableRoom[]>>({});
  /**
   * Per-property policy (Config Framework: CHECKIN_POLICY →
   * checkin.require_id_document). When TRUE, at least one complete ID
   * document (type + file) is required to check in. Re-fetched on every
   * open per the config-freshness rule; fetch failure falls back to
   * optional — the server enforces the same rule as a backstop.
   */
  const [requireIdDocumentSetting, setRequireIdDocument] = useState(false);
  /**
   * Per-property policy (checkin.require_all_guest_details): when TRUE every
   * guest row must be complete (name, gender, age, nationality, ID type +
   * number) before check-in; when FALSE only the primary guest's name is
   * mandatory. Fetch failure falls back to the lenient mode — the server
   * enforces the same rule as a backstop.
   */
  const [requireAllGuestDetailsSetting, setRequireAllGuestDetails] = useState(false);
  // What actually blocks: the property's settings, unless it completes cards later (GRC-3).
  const requireIdDocument = requireIdDocumentSetting && !grcLater;
  const requireAllGuestDetails = requireAllGuestDetailsSetting && !grcLater;
  const requireNextDestination = requireNextDestinationSetting && !grcLater;
  /**
   * Nightly inventory position per category across this stay
   * (CRS-Inventory-Shortage-and-Oversell-Design.md §7.2). Drives the shortfall
   * chip in the category dropdown and the confirm-summary warning.
   *
   * Advisory: the server re-derives the same numbers at save and refuses an
   * unacknowledged shortage, so a failed fetch loses the early warning without
   * ever letting the exposure through unnoticed.
   */
  const [inventoryImpact, setInventoryImpact] = useState<CheckInInventoryImpact | null>(null);

  useEffect(() => {
    if (!isOpen || !booking) {
      setRequireIdDocument(false);
      setRequireAllGuestDetails(false);
      return;
    }
    let cancelled = false;
    const scopes = [{ type: 'PROPERTY', id: booking.propertyId }];
    configService
      .getEffectiveValue('checkin.require_id_document', scopes)
      .then((res) => {
        if (!cancelled) {
          setRequireIdDocument((res.data?.effectiveValue ?? 'false').toLowerCase() === 'true');
        }
      })
      .catch(() => { if (!cancelled) setRequireIdDocument(false); });
    configService
      .getEffectiveValue('checkin.require_all_guest_details', scopes)
      .then((res) => {
        if (!cancelled) {
          setRequireAllGuestDetails((res.data?.effectiveValue ?? 'false').toLowerCase() === 'true');
        }
      })
      .catch(() => { if (!cancelled) setRequireAllGuestDetails(false); });
    return () => { cancelled = true; };
  }, [isOpen, booking]);

  /**
   * Nightly inventory position for the stay. Fetched on every open rather than
   * cached: another desk can commit the last room between two check-ins, and a
   * stale "no shortage" is the one answer this warning must never give.
   *
   * Amend mode does not fetch — an amendment never moves inventory.
   */
  useEffect(() => {
    if (!isOpen || !booking || isAmend) {
      setInventoryImpact(null);
      return;
    }
    let cancelled = false;
    checkInService
      .getInventoryImpact(booking.bookingId)
      .then((impact) => { if (!cancelled) setInventoryImpact(impact); })
      .catch(() => { if (!cancelled) setInventoryImpact(null); });
    return () => { cancelled = true; };
  }, [isOpen, booking, isAmend]);

  /**
   * Rooms the booking will hold in each category once this assignment is applied.
   * Counted from the ASSIGNED rows only — an unassigned slot is a no-show
   * candidate and shelters nobody, so it must not be counted as demand.
   */
  const roomsByCategory = useMemo(() => {
    const out: Record<string, number> = {};
    assignments.forEach((a) => {
      if (!a.roomId) return;
      out[a.roomCategoryId] = (out[a.roomCategoryId] ?? 0) + 1;
    });
    return out;
  }, [assignments]);

  /**
   * Nights of the stay this assignment would leave short. Recomputed as the desk
   * changes categories, so the warning tracks what is actually on screen.
   */
  const shortfalls = useMemo(
    () => computeShortfalls(inventoryImpact, roomsByCategory),
    [inventoryImpact, roomsByCategory],
  );

  /** Fetch (or reuse) available rooms for a category; records counts + room details. */
  const fetchRoomsForCategory = useCallback(async (categoryId: string): Promise<AvailableRoom[]> => {
    if (!booking || !checkInDate) return [];
    const cacheKey = `${categoryId}|${checkInDate}`;
    let rooms = availabilityCacheRef.current[cacheKey];
    if (!rooms) {
      rooms = await checkInService.getAvailableRooms(booking.propertyId, categoryId, checkInDate, auditCorrection);
      availabilityCacheRef.current[cacheKey] = rooms;
      rooms.forEach((r) => { roomInfoRef.current[r.roomId] = r; });
      setCategoryCounts((prev) => ({ ...prev, [categoryId]: rooms.length }));
    }
    return rooms;
  }, [booking, checkInDate, auditCorrection]);

  /** Prime the availability counts for every category (first Change click / auto-assign). */
  const ensureCategoryCounts = useCallback(() => {
    categoryList.forEach((c) => { void fetchRoomsForCategory(c.id); });
  }, [categoryList, fetchRoomsForCategory]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // Meal plans for the arrival-change row. Loaded with the modal rather than on
  // demand: the operator opening arrival changes is already mid-task, and a
  // dropdown that populates a moment later invites a mis-click.
  useEffect(() => {
    if (!isOpen || !booking?.propertyId) return;
    let cancelled = false;
    mealPlanService.getAll({ propertyId: booking.propertyId, pageSize: 200, includeInactive: false })
      .then((res) => {
        if (cancelled) return;
        setArrivalMealPlans((res.data || []).map((m: any) => ({
          id: m.id,
          planCode: m.planCode,
          // Set (with lunch and dinner both included) = an either/or plan such as MAP.
          defaultMajorMeal: m.includesLunch && m.includesDinner ? m.defaultMajorMeal ?? null : null,
        })));
      })
      .catch(() => { if (!cancelled) setArrivalMealPlans([]); });
    return () => { cancelled = true; };
  }, [isOpen, booking?.propertyId]);

  // A line on an either/or plan (MAP: breakfast + lunch OR dinner) — only then is
  // the guests' expected major meal asked for. Declared before the save handler,
  // whose dependency list reads it during render.
  const majorMealPlan = useMemo(() => {
    const ids = new Set(detailLines.map((l) => l.mealPlanId).filter((id): id is string => !!id));
    return arrivalMealPlans.find((m) => ids.has(m.id) && !!m.defaultMajorMeal) ?? null;
  }, [detailLines, arrivalMealPlans]);
  const hasMajorMealChoice = !!majorMealPlan;

  // ── CHECKIN_POLICY config — drives the purpose list, its default, whether
  //    the gender aggregate is asked for, and whether Proceeding To blocks.
  //    Re-fetched on every open: config can change at any time in a multi-user
  //    system, and a stale default here would be silently wrong. ──────────────
  useEffect(() => {
    if (!isOpen || !booking?.propertyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await configService.getEffectiveConfig('CHECKIN_POLICY', [
          { type: 'PROPERTY', id: booking.propertyId! },
        ]);
        if (cancelled) return;
        const rows = res.data ?? [];
        const find = (key: string) => rows.find((r) => r.configKey === key);

        const optionsRow = find('checkin.purpose_of_visit_options');
        const options = (optionsRow?.listValues ?? [])
          .map((i) => i.value)
          .filter(Boolean);
        setPurposeOptions(options);

        const defRow = find('checkin.default_purpose_of_visit');
        const fallback = defRow?.effectiveValue ?? defRow?.defaultValue ?? '';
        // Only seed the default when the operator hasn't already chosen — a
        // restore from minimize must not overwrite their selection.
        setPurposeOfVisit((prev) => prev || fallback);

        const isOn = (key: string) => {
          const row = find(key);
          return (row?.effectiveValue ?? row?.defaultValue ?? 'false').toLowerCase() === 'true';
        };
        setCaptureGenderSplit(isOn('checkin.capture_gender_split'));
        setRequireNextDestination(isOn('checkin.require_next_destination'));
        setGrcLater(isOn('checkin.grc_complete_later'));
      } catch {
        // Fail safe to the in-code defaults: never block an arrival because a
        // config read failed.
        if (!cancelled) {
          setPurposeOptions([]);
          setCaptureGenderSplit(false);
          setRequireNextDestination(false);
          setGrcLater(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, booking?.propertyId]);

  // ── Load booking detail lines on open (one assignment slot per booked room) ─
  useEffect(() => {
    if (!isOpen || !booking) return;
    // Restore from minimize: detail lines / assignments / category list are
    // restored by handleMinimize's onRestore — don't rebuild them. The ref is
    // cleared by the reset effect below (defined after this one, runs second).
    if (isRestoreRef.current) return;
    let cancelled = false;
    setLoadingBooking(true);
    availabilityCacheRef.current = {};
    (async () => {
      try {
        const response = await bookingService.getFullBooking(booking.bookingId);
        if (cancelled) return;
        const full = response.data;
        const lines: BookingDetail[] = full?.detailLines ?? [];
        setDetailLines(lines);
        // Live booking record (fresh totals/pax/status — matters after Modify Stay).
        if (full?.booking) setLiveBooking(full.booking);
        // Booking Summary card: addons + special notes (decision 7).
        setBookingAddons(full?.addons ?? []);
        setSpecialNotes(full?.booking?.specialNotes ?? '');
        // GRC contact prefill — the operator confirms rather than re-keys. Only
        // seeds empty fields so a restore from minimize keeps their edits.
        const b = full?.booking;
        if (b) {
          setGrcMobile((prev) => prev || b.guestPhone || '');
          setGrcEmail((prev) => prev || b.guestEmail || '');
        }
        // Guests panel seed: stored booking-guest rows (primary first — the
        // repository orders is_primary DESC). No stored rows → synthesize the
        // primary from the booking's contact name.
        const storedGuests = full?.guests ?? [];
        guestKeyCounter.current = 0;
        if (storedGuests.length > 0) {
          setGuestRows(storedGuests.map((g) => toGuestRow(g, `guest-${guestKeyCounter.current++}`)));
        } else {
          setGuestRows([createGuestRow(`guest-${guestKeyCounter.current++}`, {
            isPrimary: true,
            name: booking.guestFullName ?? '',
          })]);
        }
        // Expand each detail line into `numberOfRooms` assignment slots.
        const rows: AssignmentRow[] = [];
        lines.forEach((line: BookingDetail, lineIdx: number) => {
          // Upgraded lines (E1) are physically hosted in the allocated category —
          // the room picker must offer rooms of that category, not the booked one.
          // The booked category stays on the row so any difference is always visible.
          const isUpgraded = !!line.allocatedRoomCategoryId
            && line.allocatedRoomCategoryId !== line.roomCategoryId;
          for (let i = 0; i < line.numberOfRooms; i++) {
            rows.push({
              rowKey: `${line.bookingDetailId}-${lineIdx}-${i}`,
              roomCategoryId: isUpgraded ? line.allocatedRoomCategoryId! : line.roomCategoryId,
              categoryCode: isUpgraded ? (line.allocatedCategoryCode ?? line.categoryCode) : line.categoryCode,
              categoryName: isUpgraded
                ? (line.allocatedCategoryName ?? line.allocatedCategoryCode ?? '')
                : line.categoryName,
              bookedCategoryId: line.roomCategoryId,
              bookedCategoryName: line.categoryName,
              roomId: '',
            });
          }
        });
        setAssignments(rows);
        // Category list for the per-row Change control (active categories only).
        if (full?.booking?.propertyId || booking.propertyId) {
          roomCategoryService
            .getAll({ propertyId: booking.propertyId, pageSize: 100, includeInactive: false })
            .then((catRes) => {
              if (cancelled) return;
              setCategoryList((catRes.data || []).map((c: any) => ({
                id: c.id,
                name: c.categoryName ?? c.name ?? c.categoryCode,
                // The only ranking the system holds — used to tell an upgrade
                // from a downgrade. A convention, not a guarantee: where two
                // categories share an order, the change is reported neutrally.
                displayOrder: c.displayOrder,
              })));
            })
            .catch(() => { /* Change control simply stays unavailable */ });
        }
      } catch {
        if (!cancelled) {
          notificationService.toast.error('Failed to load booking details.');
        }
      } finally {
        if (!cancelled) setLoadingBooking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // reloadTick re-runs the load after a Modify Stay save (slots + guests +
    // totals rebuild from the amended booking).
  }, [isOpen, booking, reloadTick]);

  // ── Reset form on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    // Restore from minimize: keep the restored values + snapshot. Clearing
    // the flag HERE (the last on-open effect that checks it) re-arms the
    // normal reset for the next fresh open.
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const now = new Date();
    const date = toDateInput(now);
    const time = toTimeInput(now);
    setCheckInDate(date);
    setCheckInTime(time);
    setNotes('');
    // Fallback gender split defaults: adults assumed male until the operator
    // splits the count (matches front-desk practice). Superseded entirely
    // once every adult guest row carries a gender.
    const defaultMale = booking?.numAdults != null ? String(booking.numAdults) : '';
    const defaultAdults = booking?.numAdults != null ? String(booking.numAdults) : '';
    const defaultChildren = booking?.numChildren != null ? String(booking.numChildren) : '0';
    setNumMale(defaultMale);
    setNumFemale('0');
    setNumAdultsIn(defaultAdults);
    setNumChildrenIn(defaultChildren);
    setNumInfantsIn('0');
    setOccupancyOverrideReason('');
    setMajorMealPref('');
    setNoShowConfirmed(false);
    setNoShowRemarks('');
    paxTouchedRef.current = false;
    // GRC + amend state — cleared on every fresh open. Without this the
    // previous booking's card details (worst: an overridden GRC number)
    // leak into the next session in the same tab.
    setAmendRecord(null);
    setGrcNumber('');
    setGrcNumberEditing(false);
    setGrcAddress('');
    setGrcMobile('');
    setGrcEmail('');
    setPurposeOfVisit('');
    setPurposeNote('');
    setNextDestination('');
    setRequestText('');
    setLiveBooking(booking ?? null);
    setChargingOpen(false);
    setChargingFolio(null);
    setPendingCharging(null);
    setArrivalOpen(false);
    setArrivalEdits({});
    setActiveSection('guests');
    setRoomViewMode('list');
    setBoardLists({});
    setFieldErrors({});
    initialSnapshotRef.current = JSON.stringify({
      date, time,
      notes: '',
      numMale: defaultMale,
      numFemale: '0',
      pax: [defaultAdults, defaultChildren, '0'],
      assignments: [],
      guests: [],
    });
  }, [isOpen, booking]);

  // ── FO change-request thread — loaded on open and after each submit ─────
  // Decided items stay in the thread (the response is the point of the loop);
  // the list is short by nature, so no pagination.
  useEffect(() => {
    if (!isOpen || !booking) return;
    let cancelled = false;
    approvalService
      .getAll({
        bookingId: booking.bookingId,
        approvalTypes: ['FO_CHANGE_REQUEST'],
        hideDecided: false,
        pageSize: 10,
      })
      .then((res) => { if (!cancelled) setChangeRequests(res.data ?? []); })
      .catch(() => { /* the thread simply stays empty */ });
    return () => { cancelled = true; };
  }, [isOpen, booking, requestsTick]);

  const handleSendRequest = useCallback(async () => {
    if (!booking || !requestText.trim()) return;
    setRequestSending(true);
    try {
      const amount = requestAmount.trim() === '' ? null : Number(requestAmount);
      await checkInService.raiseChangeRequest(booking.bookingId, requestText.trim(), amount, amount != null ? requestBasis : null);
      notificationService.toast.success('Request sent to booking staff.');
      setRequestText('');
      setRequestAmount('');
      setRequestsTick((t) => t + 1);
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message || err?.message || 'Failed to send the request.',
      );
    } finally {
      setRequestSending(false);
    }
  }, [booking, requestText, requestAmount, requestBasis]);

  // ── Amend mode: the stay's folio, for the Room charging chip ────────────
  useEffect(() => {
    if (!isOpen || !isAmend || !booking || !canControlRoomCharging()) return;
    let cancelled = false;
    folioService.getByBooking(booking.bookingId)
      .then((f) => { if (!cancelled) setChargingFolio(f); })
      .catch(() => { /* no folio: the chip stays hidden; the day sheet flags the missing account */ });
    return () => { cancelled = true; };
  }, [isOpen, isAmend, booking]);

  // ── Amend mode: prefill from the stored check-in record ─────────────────
  // Runs after the reset effect (declared below it) and overwrites the
  // "fresh check-in" defaults with the card's stored values. Guest rows come
  // from the booking-load effect — the stored booking_guest rows ARE the
  // check-in's guests, so no separate seed is needed.
  useEffect(() => {
    if (!isOpen || !isAmend || !booking) return;
    if (isRestoreRef.current) return; // restore keeps its own captured state
    let cancelled = false;
    (async () => {
      try {
        const rec = await checkInService.getByBooking(booking.bookingId);
        if (cancelled) return;
        if (!rec) {
          notificationService.toast.error('No check-in record found for this booking.');
          return;
        }
        setAmendRecord(rec);
        setCheckInDate(rec.checkInDate?.slice(0, 10) ?? '');
        setCheckInTime((rec.checkInTime ?? '').slice(0, 5));
        setNotes(rec.checkInNotes ?? '');
        if (rec.numMale != null) setNumMale(String(rec.numMale));
        if (rec.numFemale != null) setNumFemale(String(rec.numFemale));
        if (rec.numAdults != null) setNumAdultsIn(String(rec.numAdults));
        if (rec.numChildren != null) setNumChildrenIn(String(rec.numChildren));
        setNumInfantsIn(rec.numInfants != null ? String(rec.numInfants) : '0');
        // The recorded counts are authoritative — stop the booked-pax
        // re-defaulting effects from overwriting them.
        paxTouchedRef.current = true;
        setGrcNumber(rec.grcNumber ?? '');
        setPurposeOfVisit(rec.purposeOfVisit ?? '');
        setPurposeNote(rec.purposeOfVisitNote ?? '');
        setNextDestination(rec.nextDestination ?? '');
        setGrcAddress(rec.grcAddress ?? '');
        setGrcMobile(rec.grcMobile ?? '');
        setGrcEmail(rec.grcEmail ?? '');
      } catch {
        if (!cancelled) notificationService.toast.error('Failed to load the check-in record.');
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isAmend, booking]);

  /*
   * Re-default the actual-pax counts when the BOOKED pax changes underneath us.
   *
   * The seeding effect above keys on the `booking` PROP, which never changes
   * once the modal is open. A Modify Stay edit refreshes `liveBooking` instead,
   * so the Occupancy field kept its original value while the "(booked N)" hint
   * beside it showed the new one — the same row contradicting itself, and the
   * source of the pax mismatches.
   *
   * Only re-defaults while the operator has not keyed the counts. Once they
   * have, their number stands: they are looking at the guests.
   */
  const bookedPaxKey = `${liveBooking?.numAdults ?? booking?.numAdults ?? ''}|${liveBooking?.numChildren ?? booking?.numChildren ?? ''}`;
  useEffect(() => {
    if (!isOpen || paxTouchedRef.current || isRestoreRef.current) return;
    const src = liveBooking ?? booking;
    if (!src) return;
    setNumAdultsIn(src.numAdults != null ? String(src.numAdults) : '');
    setNumChildrenIn(src.numChildren != null ? String(src.numChildren) : '0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bookedPaxKey]);

  // Occupancy auto-sync from the named guest rows — ONLY while the property
  // requires every guest to be entered (checkin.require_all_guest_details).
  // Under that policy the rows are the authoritative headcount, so the counts
  // follow them. Without it the desk typically names just the primary, and
  // deriving from the rows showed 1 adult on a 4-pax booking — so the BOOKED
  // pax stays the default instead. A manual edit wins either way.
  useEffect(() => {
    if (!isOpen || !requireAllGuestDetails || paxTouchedRef.current || isRestoreRef.current) return;
    const named = guestRows.filter((g) => g.name.trim());
    if (named.length === 0) return;
    setNumAdultsIn(String(named.filter((g) => g.guestType === 'Adult').length));
    setNumChildrenIn(String(named.filter((g) => g.guestType === 'Child').length));
    setNumInfantsIn(String(named.filter((g) => g.guestType === 'Infant').length));
  }, [isOpen, requireAllGuestDetails, guestRows]);

  // ── Track dirty state for unsaved-changes confirmation ──────────────────
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    const current = JSON.stringify({
      date: checkInDate, time: checkInTime,
      notes,
      numMale,
      numFemale,
      pax: [numAdultsIn, numChildrenIn, numInfantsIn],
      assignments: assignments.map((a) => a.roomId),
      guests: serializeGuestRows(guestRows),
    });
    return current !== initialSnapshotRef.current;
  }, [checkInDate, checkInTime, notes, numMale, numFemale, numAdultsIn, numChildrenIn, numInfantsIn, assignments, guestRows]);

  // ── Over-occupancy detection (mirrors CheckInService's guard) ───────────
  // Ceiling per booked line: the ALLOCATED (upgrade) category's max_occupancy
  // when the line is upgraded — the guests occupy that room — else the booked
  // category's. Base occupancy is only a legacy fallback for rows the view
  // predates. Infants do not count toward the licensing ceiling (the server
  // sums adults + children only).
  const occupancyCapacity = useMemo(() =>
    detailLines.reduce((sum, l) => {
      const ceiling = l.allocatedMaxOccupancy || l.maxOccupancy
        || (l.baseOccupancy > 0 ? l.baseOccupancy : 2);
      return sum + ceiling * (l.numberOfRooms || 0);
    }, 0), [detailLines]);
  const actualPaxOnSite = (Number(numAdultsIn) || 0) + (Number(numChildrenIn) || 0);
  const overOccupancy = occupancyCapacity > 0 && actualPaxOnSite > occupancyCapacity;

  // ── Per-row loadOptions for the room SyutSelect ─────────────────────────
  /**
   * Upgrade / downgrade / neither, from display_order. Returns 'different' when
   * the ranking cannot answer — equal orders, or either side missing one — so a
   * property that orders its categories arbitrarily gets an honest neutral label
   * rather than a confidently wrong one.
   */
  const classifyChange = useCallback((bookedId: string, targetId: string):
    'same' | 'upgrade' | 'downgrade' | 'different' => {
    if (bookedId === targetId) return 'same';
    const a = categoryList.find((c) => c.id === bookedId)?.displayOrder;
    const b = categoryList.find((c) => c.id === targetId)?.displayOrder;
    if (a == null || b == null || a === b) return 'different';
    return b > a ? 'upgrade' : 'downgrade';
  }, [categoryList]);

  /**
   * Rooms for the picker, GROUPED BY CATEGORY — booked category first, other
   * categories below. One control replaces the old hidden "Other category…"
   * dropdown plus picker, so an upgrade is a single selection and the keyboard
   * path still works (type a room number, pick it).
   *
   * Only the booked group is offered without the category-change permission;
   * the operator otherwise sees rooms they are not allowed to allocate.
   */
  const buildLoadOptions = useCallback(
    (rowKey: string, roomCategoryId: string) =>
      async (inputValue: string): Promise<SelectOption[]> => {
        const row = assignments.find((a) => a.rowKey === rowKey);
        const bookedId = row?.bookedCategoryId ?? roomCategoryId;
        const takenInThisModal = new Set(
          assignments.filter((a) => a.rowKey !== rowKey && a.roomId).map((a) => a.roomId),
        );

        const ids = canChangeCategory && categoryList.length > 0
          ? [bookedId, ...categoryList.map((c) => c.id).filter((id) => id !== bookedId)]
          : Array.from(new Set([roomCategoryId, bookedId]));

        // Flat, category-annotated, booked category first. NOT react-select
        // option groups: SyutSelect resolves the selected value with a flat
        // pool.find(o => o.value === value), and a group entry has no `value`,
        // so a room picked from a group would leave the control blank.
        const out: SelectOption[] = [];
        for (const catId of ids) {
          const rooms = await fetchRoomsForCategory(catId);
          const filtered = (inputValue
            ? rooms.filter((r) => r.roomNumber.toLowerCase().includes(inputValue.toLowerCase()))
            : rooms
          ).filter((r) => !takenInThisModal.has(r.roomId));
          if (filtered.length === 0) continue;

          const catName = categoryList.find((c) => c.id === catId)?.name
            ?? (catId === bookedId ? row?.bookedCategoryName : undefined)
            ?? 'Category';
          const kind = classifyChange(bookedId, catId);
          const tag = kind === 'same' ? `${catName} · booked`
            : kind === 'upgrade' ? `${catName} · upgrade`
            : kind === 'downgrade' ? `${catName} · DOWNGRADE`
            : `${catName} · other`;

          // Room number leads — operators type room numbers, and react-select
          // filters on the label text.
          filtered.forEach((r) => out.push({
            value: r.roomId,
            label: `${roomLabel(r)} — ${tag}`,
          }));
        }
        return out;
      },
    [fetchRoomsForCategory, assignments, canChangeCategory, categoryList, classifyChange],
  );

  /**
   * Selecting a room from the grouped picker may implicitly change the row's
   * category. Resolving the room's own category here — rather than trusting the
   * row's current one — is what keeps the badge, categoryChanges and the save
   * summary correct after a cross-category pick.
   */
  const resolveRoomCategory = useCallback((roomId: string): string | undefined => {
    const cached = roomInfoRef.current[roomId];
    return cached?.roomCategoryId;
  }, []);

  // ── Category change per row (permission-gated) ──────────────────────────

  const handleCategoryChange = useCallback((rowKey: string, categoryId: string | null) => {
    if (!categoryId) return;
    const target = categoryList.find((c) => c.id === categoryId);
    setAssignments((prev) => prev.map((a) => {
      if (a.rowKey !== rowKey) return a;
      // Picking the booked category again = revert; room resets either way
      // because the previous selection belongs to another category.
      return {
        ...a,
        roomCategoryId: categoryId,
        categoryName: categoryId === a.bookedCategoryId ? a.bookedCategoryName : target?.name,
        roomId: '',
      };
    }));
    setChangeOpenFor(null);
    clearFieldError(`assignment-${rowKey}`);
  }, [categoryList, clearFieldError]);

  // ── Auto-assign: fill every empty slot with the first free room of its
  //    current category (non-dirty rooms first). One click for the common case.
  const [autoAssigning, setAutoAssigning] = useState(false);

  /** Compute room picks for every empty slot in `rows`. Shared by the manual
   *  Auto-assign button and the assign-on-open flow. */
  const computeAutoPicks = useCallback(async (rows: AssignmentRow[]): Promise<Record<string, string>> => {
    const taken = new Set(rows.filter((a) => a.roomId).map((a) => a.roomId));
    const picks: Record<string, string> = {};
    for (const row of rows) {
      if (row.roomId) continue;
      const rooms = await fetchRoomsForCategory(row.roomCategoryId);
      const pick = [...rooms]
        .sort((a, b) => (a.housekeepingStatusCode === 'ROOM_DIRTY' ? 1 : 0)
          - (b.housekeepingStatusCode === 'ROOM_DIRTY' ? 1 : 0))
        .find((r) => !taken.has(r.roomId));
      if (pick) { picks[row.rowKey] = pick.roomId; taken.add(pick.roomId); }
    }
    return picks;
  }, [fetchRoomsForCategory]);

  const handleAutoAssign = useCallback(async () => {
    setAutoAssigning(true);
    try {
      const picks = await computeAutoPicks(assignments);
      if (Object.keys(picks).length === 0) {
        notificationService.toast.warning('No free rooms left to auto-assign.');
        return;
      }
      setAssignments((prev) => prev.map((a) => (picks[a.rowKey] ? { ...a, roomId: picks[a.rowKey] } : a)));
      setFieldErrors((prev) => {
        const next = { ...prev };
        Object.keys(picks).forEach((k) => delete next[`assignment-${k}`]);
        return next;
      });
    } finally {
      setAutoAssigning(false);
    }
  }, [assignments, computeAutoPicks]);

  // ── Auto-assign ON OPEN: as soon as the assignment slots exist, pre-fill
  //    them so a clean check-in is review-and-confirm, not build-from-scratch.
  //    Afterwards the dirty-tracking snapshot is re-baselined to the assigned
  //    state so simply opening the modal never triggers the discard prompt.
  const autoAssignInitRef = useRef(false);
  useEffect(() => { if (!isOpen) autoAssignInitRef.current = false; }, [isOpen]);
  useEffect(() => {
    if (!isOpen || loadingBooking || autoAssignInitRef.current || isAmend) return;
    if (assignments.length === 0) return;
    autoAssignInitRef.current = true;
    (async () => {
      let rows = assignments;
      if (assignments.every((a) => !a.roomId)) {
        setAutoAssigning(true);
        try {
          const picks = await computeAutoPicks(assignments);
          if (Object.keys(picks).length > 0) {
            rows = assignments.map((a) => (picks[a.rowKey] ? { ...a, roomId: picks[a.rowKey] } : a));
            setAssignments(rows);
          }
        } catch { /* fall through — operator assigns manually */ }
        finally { setAutoAssigning(false); }
      }
      initialSnapshotRef.current = JSON.stringify({
        date: checkInDate, time: checkInTime,
        notes,
        numMale,
        numFemale,
        pax: [numAdultsIn, numChildrenIn, numInfantsIn],
        assignments: rows.map((a) => a.roomId),
        guests: serializeGuestRows(guestRows),
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loadingBooking, assignments, computeAutoPicks]);

  // ── Board view: load free rooms for every category the booking occupies ──
  useEffect(() => {
    if (!isOpen || roomViewMode !== 'board') return;
    let cancelled = false;
    // Assignment categories PLUS any the operator has expanded. Expanding used to
    // call fetchRoomsForCategory directly, which caches the rooms and the count
    // but never writes boardLists — so the panel sat on "Loading rooms…" forever.
    // One fetch path, driven by this effect, is the fix.
    const catIds = Array.from(new Set([
      ...assignments.map((a) => a.roomCategoryId),
      ...Object.keys(openCategories).filter((id) => openCategories[id]),
    ]));
    (async () => {
      const entries = await Promise.all(
        catIds.map(async (id) => [id, await fetchRoomsForCategory(id)] as const),
      );
      // MERGE, not replace: replacing dropped every expanded category's rooms
      // the moment assignments changed.
      if (!cancelled) setBoardLists((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => { cancelled = true; };
  }, [isOpen, roomViewMode, assignments, openCategories, fetchRoomsForCategory]);

  /**
   * Board tap: an assigned room releases its slot; a free room fills the
   * FIRST empty slot of its category. Slots are fixed by the booking, so a
   * full category asks the operator to release a room first.
   */
  const handleBoardRoomTap = useCallback((categoryId: string, room: AvailableRoom) => {
    const assignedRow = assignments.find((a) => a.roomId === room.roomId);
    if (assignedRow) {
      setAssignments((prev) => prev.map((a) => (a.rowKey === assignedRow.rowKey ? { ...a, roomId: '' } : a)));
      return;
    }
    // A slot already pointing at this category takes it directly.
    let target = assignments.find((a) => a.roomCategoryId === categoryId && !a.roomId);

    // Otherwise the operator tapped a room in ANOTHER category — an upgrade (or
    // downgrade). Move an empty slot onto that category rather than refusing:
    // this is the whole point of showing every category. Setting roomCategoryId
    // here is what keeps the badge, categoryChanges and the save-confirmation
    // summary honest; assigning the room alone would be a silent, unaudited
    // category change.
    if (!target) {
      const spare = assignments.find((a) => !a.roomId);
      if (!spare) {
        notificationService.toast.info(
          'Every room is already assigned — tap an assigned room to release it first.',
        );
        return;
      }
      if (!canChangeCategory) {
        notificationService.toast.warning(
          'You do not have permission to allocate from another room category.',
        );
        return;
      }
      const targetName = categoryList.find((c) => c.id === categoryId)?.name;
      setAssignments((prev) => prev.map((a) => (a.rowKey === spare.rowKey
        ? {
            ...a,
            roomCategoryId: categoryId,
            categoryName: categoryId === a.bookedCategoryId ? a.bookedCategoryName : targetName,
            roomId: room.roomId,
          }
        : a)));
      clearFieldError(`assignment-${spare.rowKey}`);
      return;
    }

    setAssignments((prev) => prev.map((a) => (a.rowKey === target!.rowKey ? { ...a, roomId: room.roomId } : a)));
    clearFieldError(`assignment-${target.rowKey}`);
  }, [assignments, clearFieldError, canChangeCategory, categoryList]);

  /**
   * Board ordering: categories a room was actually booked into come first (that
   * is the 95% case and should need no hunting), then everything else by the
   * property's own display order.
   */
  const boardCategories = useMemo(() => {
    const bookedIds = new Set(assignments.map((a) => a.bookedCategoryId));
    const inUse = new Set(assignments.map((a) => a.roomCategoryId));
    const known = categoryList.length > 0
      ? categoryList
      : // Category master not loaded (no permission / failed fetch): fall back to
        // exactly what the booking uses, i.e. today's behaviour.
        Array.from(inUse).map((id) => ({
          id,
          name: assignments.find((a) => a.roomCategoryId === id)?.categoryName ?? 'Category',
          displayOrder: undefined as number | undefined,
        }));
    const rank = (c: { id: string; displayOrder?: number }) =>
      (bookedIds.has(c.id) ? 0 : inUse.has(c.id) ? 1 : 2);
    return [...known].sort((a, b) =>
      rank(a) - rank(b)
      || (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999)
      || a.name.localeCompare(b.name));
  }, [assignments, categoryList]);

  // ── Derived: rows whose category differs from booked + dirty assignments ──
  const categoryChanges = useMemo(
    () => assignments.filter((a) => a.roomCategoryId !== a.bookedCategoryId),
    [assignments],
  );
  const dirtyAssignedRooms = useMemo(
    () => assignments
      .map((a) => (a.roomId ? roomInfoRef.current[a.roomId] : undefined))
      .filter((r): r is AvailableRoom => !!r && r.housekeepingStatusCode === 'ROOM_DIRTY'),
    // roomInfoRef is a ref — assignments changing is the only trigger we need.
    [assignments],
  );

  const handleAssignmentChange = useCallback(
    (rowKey: string, value: string | string[] | null) => {
      const roomId = (value as string) || '';
      // The grouped picker can hand back a room from ANOTHER category. Follow the
      // room's own category onto the row — otherwise the change is invisible to
      // the badge, categoryChanges and the save confirmation, i.e. an unaudited
      // upgrade.
      const roomCat = roomId ? resolveRoomCategory(roomId) : undefined;
      setAssignments((prev) =>
        prev.map((a) => {
          if (a.rowKey !== rowKey) return a;
          if (!roomCat || roomCat === a.roomCategoryId) return { ...a, roomId };
          return {
            ...a,
            roomId,
            roomCategoryId: roomCat,
            categoryName: roomCat === a.bookedCategoryId
              ? a.bookedCategoryName
              : categoryList.find((c) => c.id === roomCat)?.name,
          };
        }),
      );
      clearFieldError(`assignment-${rowKey}`);
    },
    [clearFieldError, resolveRoomCategory, categoryList],
  );

  // ── Close (with dirty guard) ────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (submitting) return;
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, submitting, onClose]);

  // ── Minimize (enterprise template) ─────────────────────────────────────
  // Captures the ENTIRE form session in the closure (assignments, docs,
  // registration fields, snapshot) so a fresh open for another booking can't
  // overwrite it; onRestore puts everything back and asks the parent screen
  // to re-open the modal for this booking.
  const handleMinimize = useCallback(() => {
    if (!booking || !tabId) return;
    const savedBooking = booking;
    const saved = {
      detailLines, assignments, categoryList, categoryCounts,
      checkInDate, checkInTime, guestRows, notes,
      numMale, numFemale, roomViewMode,
      numAdultsIn, numChildrenIn, numInfantsIn, occupancyOverrideReason, majorMealPref,
      paxTouched: paxTouchedRef.current,
      bookingAddons, specialNotes, liveBooking,
      guestKeyCounter: guestKeyCounter.current,
      activeSection,
      // GRC card fields — captured explicitly now that the fresh-open reset
      // clears them (they previously survived restores only by accident).
      grcNumber, grcAddress, grcMobile, grcEmail,
      purposeOfVisit, purposeNote, nextDestination,
      amendRecord, requestText,
    };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? isDirty : false;
    minimize({
      id: `${tabId}-${isAmend ? 'amend' : 'checkin'}-modal-${savedBooking.bookingNumber}`,
      title: `${isAmend ? 'Edit Check-In' : 'Check-In'} - ${savedBooking.bookingNumber}`,
      moduleName: moduleName ?? 'Check-In',
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        autoAssignInitRef.current = true;   // skip auto-assign + snapshot re-baseline
        setDetailLines(saved.detailLines);
        setAssignments(saved.assignments);
        setCategoryList(saved.categoryList);
        setCategoryCounts(saved.categoryCounts);
        setCheckInDate(saved.checkInDate);
        setCheckInTime(saved.checkInTime);
        setGuestRows(saved.guestRows);
        setNotes(saved.notes);
        setNumMale(saved.numMale);
        setNumFemale(saved.numFemale);
        setNumAdultsIn(saved.numAdultsIn);
        setNumChildrenIn(saved.numChildrenIn);
        setNumInfantsIn(saved.numInfantsIn);
        setOccupancyOverrideReason(saved.occupancyOverrideReason);
        setMajorMealPref(saved.majorMealPref);
        paxTouchedRef.current = saved.paxTouched;
        setRoomViewMode(saved.roomViewMode);
        setBookingAddons(saved.bookingAddons);
        setSpecialNotes(saved.specialNotes);
        setLiveBooking(saved.liveBooking);
        guestKeyCounter.current = saved.guestKeyCounter;
        setActiveSection(saved.activeSection);
        setGrcNumber(saved.grcNumber);
        setGrcAddress(saved.grcAddress);
        setGrcMobile(saved.grcMobile);
        setGrcEmail(saved.grcEmail);
        setPurposeOfVisit(saved.purposeOfVisit);
        setPurposeNote(saved.purposeNote);
        setNextDestination(saved.nextDestination);
        setAmendRecord(saved.amendRecord);
        setRequestText(saved.requestText);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreCheckIn?.(savedBooking);
      },
    });
    onClose();
  }, [booking, tabId, moduleName, detailLines, assignments, categoryList, categoryCounts,
      checkInDate, checkInTime, guestRows, notes, numMale, numFemale, roomViewMode,
      numAdultsIn, numChildrenIn, numInfantsIn, occupancyOverrideReason, majorMealPref,
      bookingAddons, specialNotes, liveBooking, activeSection,
      grcNumber, grcAddress, grcMobile, grcEmail,
      purposeOfVisit, purposeNote, nextDestination, amendRecord, requestText, isAmend,
      isDirty, minimize, onClose, onRestoreCheckIn]);

  // ── Validate + show confirm ─────────────────────────────────────────────
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!checkInDate) errors.checkInDate = 'Check-in date is required.';
    if (!checkInTime) errors.checkInTime = 'Check-in time is required.';
    if (auditCorrection && !correctionReason.trim()) errors.correctionReason = 'Say why the check-in was missed.';

    // PARTIAL ARRIVAL (BR-CHKIN-002 revised, review 2026-08-11) — full
    // ACCOUNTING, not full allocation: every unassigned booked room must be
    // explicitly confirmed as a no-show with a remark. There is no "held"
    // state — an uncertain arrival gets its room allocated with the key kept
    // ready (manual FO management). At least ONE room must be assigned;
    // nobody arriving is the booking-level Mark as No-Show flow.
    if (!isAmend && assignments.length > 0 && assignments.every((a) => !a.roomId)) {
      assignments.forEach((a) => {
        errors[`assignment-${a.rowKey}`] = 'Assign at least one room to check in. If nobody arrived, use Mark as No-Show.';
      });
    }
    const unassignedCount = assignments.filter((a) => !a.roomId).length;
    if (!isAmend && unassignedCount > 0 && assignments.some((a) => a.roomId)) {
      if (!noShowConfirmed) {
        errors.noShowConfirm =
          `${unassignedCount} room${unassignedCount !== 1 ? 's are' : ' is'} not assigned — confirm ${unassignedCount !== 1 ? 'them' : 'it'} as No-Show, or assign the remaining room${unassignedCount !== 1 ? 's' : ''}.`;
      } else if (!noShowRemarks.trim()) {
        errors.noShowRemarks = 'A no-show remark is required — booking staff price from it.';
      }
    }

    // No duplicate rooms in the same check-in.
    if (!isAmend) {
      const seen = new Set<string>();
      assignments.forEach((a) => {
        if (a.roomId) {
          if (seen.has(a.roomId)) {
            errors[`assignment-${a.rowKey}`] = 'Duplicate — already assigned in this check-in.';
          }
          seen.add(a.roomId);
        }
      });
    }

    // Guests panel — shared validation (primary name, per-row completeness
    // when require_all_guest_details is ON, upload when require_id_document).
    Object.assign(errors, validateGuestRows(guestRows, { requireAllGuestDetails, requireIdDocument }));

    // Over-occupancy needs a recorded reason (tables/082) — the server rejects
    // the check-in without one. Caught here so the operator lands on the field
    // instead of reading a failed-submit toast.
    if (overOccupancy && !occupancyOverrideReason.trim()) {
      errors.occupancyOverrideReason =
        `${actualPaxOnSite} guests exceed the rooms' maximum occupancy of ${occupancyCapacity} — record a reason to proceed.`;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Jump to the first section (nav order) that carries an error, so the
      // operator lands ON the problem instead of hunting the red badge.
      const sectionOf = (k: string): string =>
        k === 'checkInDate' || k === 'checkInTime' || k === 'correctionReason' ? 'arrival'
          : k.startsWith('assignment-') || k.startsWith('noShow') ? 'rooms'
          : 'guests';
      const first = ['arrival', 'guests', 'grc', 'rooms', 'billing']
        .find((s) => Object.keys(errors).some((k) => sectionOf(k) === s));
      if (first) setActiveSection(first);
      return;
    }
    setFieldErrors({});

    // Amend commits directly — the confirm summary's warnings (balance,
    // dirty room, category change) are check-in concerns, not amendment ones.
    if (isAmend) {
      confirmSaveRef.current();
      return;
    }

    // One-click check-in when nothing needs a second look. The confirm
    // summary only appears when a warning exists: outstanding balance,
    // early/backdated date, a dirty room, or a category change.
    const bk = liveBooking ?? booking;
    if (bk) {
      const total     = bk.finalGrandTotal ?? bk.grandTotal ?? 0;
      const collected = bk.advanceReceived ?? 0;
      const balance   = bk.balanceDue ?? Math.max(0, total - collected);
      const early     = !!bk.checkInDate && checkInDate < bk.checkInDate.slice(0, 10);
      const back      = !!checkInDate && checkInDate < toDateInput(new Date());
      const hasWarnings = balance > 0 || early || back
        || dirtyAssignedRooms.length > 0
        || assignments.some((a) => !a.roomId)
        || assignments.some((a) => a.roomCategoryId !== a.bookedCategoryId)
        // A projected shortage is never taken silently (BR-SHORT-002). Checking in
        // does not create it — it removes the degrees of freedom for solving it,
        // which is exactly why this is the moment to make the desk look.
        || shortfalls.length > 0;
      if (!hasWarnings) {
        confirmSaveRef.current();
        return;
      }
    }
    setSaveConfirmOpen(true);
  }, [checkInDate, checkInTime, auditCorrection, correctionReason, assignments, guestRows, booking, liveBooking, dirtyAssignedRooms, requireIdDocument, requireAllGuestDetails,
      overOccupancy, occupancyOverrideReason, actualPaxOnSite, occupancyCapacity, isAmend,
      noShowConfirmed, noShowRemarks, shortfalls]);

  // ── Confirmed save ─────────────────────────────────────────────────────
  const handleConfirmSave = useCallback(async () => {
    if (!booking) return;
    setSaveConfirmOpen(false);
    setSubmitting(true);
    // Printing the card after check-in needs its tab opened inside this click,
    // before any await, or the browser blocks it (GRC-2).
    const grcTab = printCardAfter && !isAmend && !auditCorrection ? reserveTab() : null;
    // Guests payload: rows with a name. The server upserts booking-guest
    // rows, derives the registration aggregates from them, attaches per-guest
    // uploads, and mirrors the primary guest's ID onto tbl_checkin.
    const guests = buildGuestsPayload(guestRows);
    // Fallback gender aggregates — the server prefers values derived from the
    // guest rows; these only take effect when the rows lack gender data.
    const adultsRows = guestRows.filter((g) => g.name.trim() && g.guestType === 'Adult');
    const gendersComplete = adultsRows.length > 0 && adultsRows.every((g) => !!g.gender);

    // ── Amend mode — corrects the stored card. No rooms, no GRC number
    // (the card keeps its identity), guests as a full replacement set. ──
    if (isAmend) {
      const amendPayload: AmendCheckInRequest = {
        checkInDate,
        checkInTime: checkInTime.length === 5 ? `${checkInTime}:00` : checkInTime,
        checkInNotes: notes.trim() || undefined,
        guests: guests.length > 0 ? guests : undefined,
        numMale: !gendersComplete && numMale.trim() !== '' ? Number(numMale) : undefined,
        numFemale: !gendersComplete && numFemale.trim() !== '' ? Number(numFemale) : undefined,
        numAdults: numAdultsIn.trim() !== '' ? Number(numAdultsIn) : undefined,
        numChildren: numChildrenIn.trim() !== '' ? Number(numChildrenIn) : undefined,
        numInfants: numInfantsIn.trim() !== '' ? Number(numInfantsIn) : undefined,
        occupancyOverrideReason: overOccupancy ? occupancyOverrideReason.trim() || undefined : undefined,
        purposeOfVisit: purposeOfVisit.trim() || undefined,
        purposeOfVisitNote:
          purposeOfVisit.trim().toLowerCase() === 'other' ? purposeNote.trim() || undefined : undefined,
        nextDestination: nextDestination.trim() || undefined,
        grcAddress: grcAddress.trim() || undefined,
        grcMobile: (grcMobile.trim() || primaryGuestRow?.phone?.trim()) || undefined,
        grcEmail: (grcEmail.trim() || primaryGuestRow?.email?.trim()) || undefined,
      };
      try {
        await checkInService.amend(booking.bookingId, amendPayload);
        notificationService.toast.success(
          `Check-in for "${booking.bookingNumber}" updated.`,
        );
        onCheckedIn();
        onClose();
      } catch (err: any) {
        const msg = err?.response?.data?.message
          || err?.response?.data?.errors?.[0]
          || err?.message
          || 'Check-in update failed.';
        notificationService.toast.error(msg);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const payload: QuickCheckInRequest = {
      checkInDate,
      checkInTime: checkInTime.length === 5 ? `${checkInTime}:00` : checkInTime,
      checkInNotes: notes.trim() || undefined,
      guests: guests.length > 0 ? guests : undefined,
      numMale: !gendersComplete && numMale.trim() !== '' ? Number(numMale) : undefined,
      numFemale: !gendersComplete && numFemale.trim() !== '' ? Number(numFemale) : undefined,
      // Actual pax on site — always sent (pre-filled/auto-synced client-side).
      numAdults: numAdultsIn.trim() !== '' ? Number(numAdultsIn) : undefined,
      numChildren: numChildrenIn.trim() !== '' ? Number(numChildrenIn) : undefined,
      numInfants: numInfantsIn.trim() !== '' ? Number(numInfantsIn) : undefined,
      // Only sent while the counts are actually over the ceiling — a reason
      // typed and then made moot by a pax correction must not be recorded.
      occupancyOverrideReason: overOccupancy ? occupancyOverrideReason.trim() || undefined : undefined,
      // Only for a MAP stay; empty = the plan's expected meal.
      majorMealPref: hasMajorMealChoice && majorMealPref ? majorMealPref : undefined,
      // GRC. Only send a number when the operator overrode it — an empty string
      // would otherwise suppress the server's generated series.
      grcNumber: grcNumber.trim() || undefined,
      purposeOfVisit: purposeOfVisit.trim() || undefined,
      purposeOfVisitNote:
        purposeOfVisit.trim().toLowerCase() === 'other' ? purposeNote.trim() || undefined : undefined,
      nextDestination: nextDestination.trim() || undefined,
      grcAddress: grcAddress.trim() || undefined,
      // Snapshot for the card, derived from the primary guest. Still snapshotted
      // rather than referenced: editing that row later must not rewrite a signed
      // registration card.
      grcMobile: (grcMobile.trim() || primaryGuestRow?.phone?.trim()) || undefined,
      grcEmail: (grcEmail.trim() || primaryGuestRow?.email?.trim()) || undefined,
      // Only the rooms actually ARRIVING — the server derives the per-line
      // no-show counts from what is missing (assigned + no-show = booked).
      rooms: assignments.filter((a) => a.roomId).map((a) => ({ roomId: a.roomId })),
      acceptNoShowForUnassigned: assignments.some((a) => !a.roomId) ? true : undefined,
      noShowRemarks: assignments.some((a) => !a.roomId) ? noShowRemarks.trim() || undefined : undefined,
      // Only set when a shortage was actually shown. Sending it unconditionally
      // would turn the server's backstop into a rubber stamp — the desk has to
      // have seen the nights in the confirm summary for this to mean anything.
      acceptShortage: shortfalls.length > 0 ? true : undefined,
    };
    try {
      if (auditCorrection) {
        const corrected = await auditCorrectionService.recordMissedCheckIn(booking.bookingId, {
          checkIn: payload, reason: correctionReason.trim(),
        });
        if (corrected.failures.length > 0) notificationService.toast.warning(`${corrected.message} ${corrected.failures[0]}`);
        else notificationService.toast.success(corrected.message);
        onCheckedIn();
        onClose();
        return;
      }
      const result = await checkInService.checkIn(booking.bookingId, payload);
      notificationService.toast.success(
        result.grcNumber
          ? `Booking "${booking.bookingNumber}" checked in — Registration Card ${result.grcNumber}.`
          : `Booking "${booking.bookingNumber}" checked in.`,
      );
      // The folio opened inside the check-in; room charging is set on it now,
      // through the same audited posting-control call the folio screen uses.
      if (pendingCharging) {
        try {
          const folio = await folioService.getByBooking(booking.bookingId);
          await folioService.setPostingControl(folio.folioId, pendingCharging);
        } catch (err: any) {
          notificationService.toast.warning(
            `Checked in, but room charging was not changed: ${err?.response?.data?.message || err?.message || 'the folio did not answer.'} `
              + 'Set it from the in-house row → Room Charging.',
            8000,
          );
        }
      }
      // The registration card for the guest to complete and sign (GRC-2).
      if (grcTab) {
        try {
          showPdf(await outputProfileService.grcPdf(booking.bookingId), grcTab);
        } catch (err) {
          grcTab.close();
          notificationService.toast.warning(
            `Checked in, but the registration card did not print: ${await blobErrorMessage(err, 'no reply.')} `
              + 'Print it from the in-house row → Print GRC.',
            8000,
          );
        }
      }
      onCheckedIn();
      onClose();
    } catch (err: any) {
      grcTab?.close();
      const msg = err?.response?.data?.message
        || err?.response?.data?.errors?.[0]
        || err?.message
        || 'Check-in failed.';
      notificationService.toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [booking, checkInDate, checkInTime, notes, numMale, numFemale,
      numAdultsIn, numChildrenIn, numInfantsIn, guestRows, assignments, onCheckedIn, onClose,
      overOccupancy, occupancyOverrideReason, noShowRemarks, hasMajorMealChoice, majorMealPref,
      // GRC values are read in the payload — without these the callback would
      // close over whatever they were when it was last rebuilt and submit stale
      // details for the card.
      grcNumber, grcAddress, grcMobile, grcEmail,
      purposeOfVisit, purposeNote, nextDestination, isAmend, auditCorrection, correctionReason,
      // The GRC snapshot falls back to the primary guest's contact, so the
      // callback would otherwise close over a stale row and save the wrong card.
      primaryGuestRow?.phone, primaryGuestRow?.email,
      // acceptShortage is derived from this. Omitted, the callback keeps the
      // shortfalls from whenever it was last rebuilt — so changing the category
      // could send the flag for the PREVIOUS assignment, and the server would
      // refuse a shortage the desk had just confirmed.
      shortfalls,
      // Applied to the new folio after the check-in commits.
      pendingCharging,
      printCardAfter]);
  confirmSaveRef.current = handleConfirmSave;

  // ── Guest-row helpers (shared Guests panel below) ───────────────────────
  const addGuestRow = useCallback(() => {
    setGuestRows((prev) => [...prev, createGuestRow(`guest-${guestKeyCounter.current++}`, {
      // Accompanying guests usually share the primary's country — pre-fill it.
      nationality: prev.find((g) => g.isPrimary)?.nationality ?? 'India',
      state: prev.find((g) => g.isPrimary)?.state ?? '',
    })]);
  }, []);

  const removeGuestRow = useCallback((key: string) => {
    // The primary row can't be removed — only accompanying guests.
    setGuestRows((prev) => prev.filter((g) => g.key !== key || g.isPrimary));
    setFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.endsWith(`-${key}`)) delete next[k]; });
      return next;
    });
  }, []);

  const updateGuestRow = useCallback(
    (key: string, patch: Partial<Omit<GuestRow, 'key' | 'id' | 'isPrimary'>>) => {
      setGuestRows((prev) => prev.map((g) => (g.key === key ? { ...g, ...patch } : g)));
      setFieldErrors((prev) => clearGuestRowErrors(prev, key, patch));
    },
    [],
  );
  /**
   * Exactly one primary guest, enforced by construction rather than validated
   * after the fact: setting one clears every other, so the invariant cannot be
   * broken by the UI at all.
   */
  const setPrimaryGuest = useCallback((key: string) => {
    setGuestRows((prev) => prev.map((g) => ({ ...g, isPrimary: g.key === key })));
  }, []);


  // ── Arrival changes — applied in place via the check-in permission ──────
  const handleApplyArrivalChanges = useCallback(async () => {
    if (!booking) return;
    const lines = detailLines
      .map((d) => {
        const edit = arrivalEdits[d.bookingDetailId];
        if (!edit) return null;

        /*
         * Meal plan is the ONLY thing this row changes.
         *
         * Rooms, pax and the agreed rate used to be editable here as well, which
         * gave the desk a second door onto the booking's commercial figures —
         * one that re-priced the stay without going through Edit Booking. Two
         * doors onto one fact is how the pax counts drifted: this row moved
         * BOOKED pax while the Occupancy section held ACTUAL pax, and nothing
         * reconciled them.
         *
         * Anything rate- or inventory-impacting now goes through Modify Stay
         * (Edit Booking), which re-prices properly and routes a materially worse
         * change to approval. Meal plan stays because the kitchen needs it on
         * arrival day and the server already accepts it here.
         */
        const mealPlanId = edit.mealPlanId || undefined;
        if (!mealPlanId || mealPlanId === d.mealPlanId) return null;

        return {
          bookingDetailId: d.bookingDetailId,
          mealPlanId,
          adjustmentRemarks: edit.remarks.trim() || undefined,
        };
      })
      .filter((l): l is NonNullable<typeof l> => l !== null);

    if (lines.length === 0) {
      notificationService.toast.info('Pick a different meal plan to apply a change.');
      return;
    }

    setApplyingArrival(true);
    try {
      const updated = await checkInService.applyArrivalChanges(booking.bookingId, { lines });
      setLiveBooking(updated);
      setArrivalEdits({});
      setArrivalOpen(false);
      // Slots, guests and totals must rebuild from the amended booking.
      autoAssignInitRef.current = false;
      availabilityCacheRef.current = {};
      setReloadTick((t) => t + 1);
      notificationService.toast.success(
        updated.bookingStatusCode?.toUpperCase() === 'PENDING_APPROVAL'
          ? 'Arrival changes saved — sent for approval. Check-in resumes once approved.'
          : 'Arrival changes applied.',
      );
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message || err?.message || 'Could not apply the arrival changes.',
      );
    } finally {
      setApplyingArrival(false);
    }
  }, [booking, detailLines, arrivalEdits]);

  // Legacy handler retained for the reload path.
  const handleModifyStaySaved = useCallback(() => {
    // Re-run the load effect: slots, guests, addons and totals rebuild from
    // the amended booking. Re-arm auto-assign for the fresh slot set.
    autoAssignInitRef.current = false;
    availabilityCacheRef.current = {};
    setReloadTick((t) => t + 1);
  }, []);

  /**
   * Rooms booked but not allocated tonight. BR-CHKIN-002 (revised): there is
   * no ambiguous "held" state — every unassigned room must be explicitly
   * confirmed as a NO-SHOW before the check-in saves (assigned + no-show =
   * booked). An arrival the desk still expects gets its room allocated with
   * the key kept ready — manual FO management, by design.
   */
  const unassignedRooms = assignments.filter((a) => !a.roomId).length;

  /**
   * Records money taken at the desk. Posts through the existing payment service
   * so there is one posting path however the money arrives, and runs BEFORE the
   * status flips — a failed payment must leave a CONFIRMED booking rather than
   * an in-house guest with a broken receipt.
   */
  const handleCollectPayment = useCallback(async () => {
    const amount = Number(payAmount);
    if (!booking || !Number.isFinite(amount) || amount <= 0) {
      notificationService.toast.warning('Enter the amount actually received.');
      return;
    }
    setPayPosting(true);
    try {
      await paymentService.create({
        bookingId: booking.bookingId,
        amount,
        paymentMode: payMode,
        paymentDate: toDateInput(new Date()),
        paymentReference: payReference.trim() || undefined,
        paymentType: 'Advance',
        remarks: 'Collected at check-in',
        collectionPoint: 'FRONT_OFFICE',
      });
      notificationService.toast.success(`Payment of ${amount.toLocaleString()} recorded.`);
      setPayOpen(false);
      setPayAmount('');
      setPayReference('');
      // Re-read the booking so Total / Collected / Balance reflect the receipt
      // immediately — the operator should not have to reopen the screen.
      const refreshed = await bookingService.getFullBooking(booking.bookingId);
      if (refreshed.data?.booking) setLiveBooking(refreshed.data.booking);
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message || 'Could not record the payment.',
      );
    } finally {
      setPayPosting(false);
    }
  }, [booking, payAmount, payMode, payReference]);

  if (!isOpen || !booking) return null;

  // ── UI helpers ─────────────────────────────────────────────────────────
  // Live record (post-Modify-Stay) wins over the prop for totals/pax/status.
  const bk = liveBooking ?? booking;
  const earlyArrival = bk.checkInDate && checkInDate < bk.checkInDate.slice(0, 10);
  // Backdated check-in (date before today) — legitimate for migration entry and
  // after-midnight arrivals processed from the Yesterday queue. Informational
  // only; the entered date/time is recorded as the actual check-in.
  const backdated = !!checkInDate && checkInDate < toDateInput(new Date());
  // A Modify Stay edit can push the booking back to PENDING_APPROVAL (a
  // materially-worse change) — check-in must wait for the approval then.
  // Amend mode expects the OPPOSITE status (CHECKED_IN), so the gate is off.
  const notConfirmed = !isAmend
    && !!bk.bookingStatusCode
    && bk.bookingStatusCode.toUpperCase() !== 'CONFIRMED';
  // Distinct meal plans across the booking's lines for the summary chips.
  const mealPlanChips = Array.from(new Set(
    detailLines
      .map((l) => l.mealPlanCode ?? l.defaultMealPlanCode)
      .filter((c): c is string => !!c),
  ));
  // Soft pax check (decision 2): named guest rows vs booked pax — warn, never block.
  const bookedPax = (bk.numAdults ?? 0) + (bk.numChildren ?? 0);
  const namedGuestCount = guestRows.filter((g) => g.name.trim()).length;
  const paxMismatch = bookedPax > 0 && namedGuestCount > 0 && namedGuestCount !== bookedPax;
  // Fallback gender inputs disappear once every named adult row has a gender.
  const namedAdults = guestRows.filter((g) => g.name.trim() && g.guestType === 'Adult');
  // Per-guest gender already carries this; the aggregate is only asked for
  // where a statutory return needs it (decision 7) AND the rows can't supply it.
  const showGenderFallback =
    captureGenderSplit && !(namedAdults.length > 0 && namedAdults.every((g) => !!g.gender));

  // ── What blocks check-in (decision 5) ───────────────────────────────────
  // Exactly three things, plus the pre-existing room-assignment rule. Anything
  // else on the GRC is captured but never refuses an arrival.
  const namedGuestRows = guestRows.filter((g) => g.name.trim());
  const primaryCount = namedGuestRows.filter((g) => g.isPrimary).length;
  const blockPrimary = namedGuestRows.length > 0 && primaryCount !== 1;
  const blockMobile = !(primaryGuestRow?.phone ?? '').trim() && !grcMobile.trim();
  const blockNextDest = requireNextDestination && !nextDestination.trim();
  const blockers = [
    blockPrimary && (primaryCount === 0 ? 'Mark a primary guest' : 'Only one guest can be primary'),
    blockMobile && 'Mobile number required',
    blockNextDest && 'Proceeding To required',
  ].filter(Boolean) as string[];


  /** GRC completeness — informational only; none of these refuse check-in. */
  const grcFilled = [grcAddress, purposeOfVisit, nextDestination]
    .filter((v) => v.trim()).length;

  // ── Financial state — shown in Billing AND as a persistent summary chip ──
  const totalAmt = bk.finalGrandTotal ?? bk.grandTotal ?? 0;
  const collectedAmt = bk.advanceReceived ?? 0;
  const balanceAmt = bk.balanceDue ?? Math.max(0, totalAmt - collectedAmt);
  const fmtAmt = (n: number) => n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const assignedCount = assignments.filter((a) => a.roomId).length;

  // ── Per-section validation errors (nav badges + first-error jump) ───────
  const errorSectionOf = (key: string): string =>
    key === 'checkInDate' || key === 'checkInTime' ? 'arrival'
      : key.startsWith('assignment-') ? 'rooms'
      : 'guests';
  const errorCounts: Record<string, number> = {};
  Object.keys(fieldErrors).forEach((k) => {
    const s = errorSectionOf(k);
    errorCounts[s] = (errorCounts[s] ?? 0) + 1;
  });
  // Blockers are errors too as far as the nav is concerned — the operator must
  // be able to see WHERE the disabled submit button's reason lives. The mobile
  // blocker is skipped when a guestPhone field error already counts it.
  if (blockPrimary) errorCounts['guests'] = (errorCounts['guests'] ?? 0) + 1;
  if (blockMobile && !Object.keys(fieldErrors).some((k) => k.startsWith('guestPhone-'))) {
    errorCounts['guests'] = (errorCounts['guests'] ?? 0) + 1;
  }
  if (blockNextDest) errorCounts['grc'] = (errorCounts['grc'] ?? 0) + 1;

  const blockerItems: ShellBlocker[] = [
    ...(blockPrimary
      ? [{ label: primaryCount === 0 ? 'Mark a primary guest' : 'Only one guest can be primary', sectionId: 'guests' }]
      : []),
    ...(blockMobile ? [{ label: 'Mobile number required', sectionId: 'guests' }] : []),
    ...(blockNextDest ? [{ label: 'Proceeding To required', sectionId: 'grc' }] : []),
  ];

  // ══════════════════════════════════════════════════════════════════════
  // SECTIONS
  // ══════════════════════════════════════════════════════════════════════

  const sections: ShellSection[] = [
    // ── ① Arrival & Stay — when the guest arrives, plus the one in-place
    //      amendment the desk may make (meal plan, via arrival changes). ──
    {
      id: 'arrival',
      title: 'Arrival & Stay',
      badge: earlyArrival || backdated ? 'warn' : (checkInDate && checkInTime ? 'ok' : undefined),
      errorCount: errorCounts['arrival'],
      content: (
        <>
          <div className="form-grid-2" style={{ marginBottom: '0.875rem' }}>
            <div className="form-group">
              <label className="form-label">Check-In Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${fieldErrors.checkInDate ? ' form-field-error' : ''}`}
                value={checkInDate}
                onChange={(e) => { clearFieldError('checkInDate'); setCheckInDate(e.target.value); }}
                disabled={submitting}
              />
              {fieldErrors.checkInDate && <div className="form-error-message">{fieldErrors.checkInDate}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Check-In Time <span className="text-danger">*</span></label>
              <input
                type="time"
                className={`form-input${fieldErrors.checkInTime ? ' form-field-error' : ''}`}
                value={checkInTime}
                onChange={(e) => { clearFieldError('checkInTime'); setCheckInTime(e.target.value); }}
                disabled={submitting}
              />
              {fieldErrors.checkInTime && <div className="form-error-message">{fieldErrors.checkInTime}</div>}
            </div>
          </div>

          {/* ── Early arrival warning (BR-CHKIN-004) ── */}
          {earlyArrival && (
            <div style={{
              padding: '0.5rem 0.75rem',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.35)',
              borderRadius: '6px',
              fontSize: '0.8125rem',
              marginBottom: '0.75rem',
              color: 'var(--text-primary)',
            }}>
              Early arrival — check-in date is before the planned arrival date ({bk.checkInDate?.slice(0, 10)}).
            </div>
          )}

          {auditCorrection && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{
                padding: '0.5rem 0.75rem', background: 'rgba(217, 119, 6, 0.08)', border: '1px solid rgba(217, 119, 6, 0.35)',
                borderRadius: '6px', fontSize: '0.8125rem', marginBottom: '0.5rem', color: 'var(--text-primary)',
              }}>
                Audit correction — the arrival is on a date night audit has closed. The rooms are checked against the
                stays of those nights; a stay already over checks out on its booked date; the missed nights post on
                the open date. Closed dates do not change.
              </div>
              <label className="form-label">Why was the check-in missed? <span className="text-danger">*</span></label>
              <textarea
                className={`form-input${fieldErrors.correctionReason ? ' form-field-error' : ''}`}
                rows={2}
                maxLength={180}
                value={correctionReason}
                onChange={(e) => { clearFieldError('correctionReason'); setCorrectionReason(e.target.value); }}
                placeholder="e.g. Guest arrived late at night; the desk did not enter the check-in"
                disabled={submitting}
              />
              {fieldErrors.correctionReason && <div className="form-error-message">{fieldErrors.correctionReason}</div>}
            </div>
          )}

          {backdated && !auditCorrection && (
            <div style={{
              padding: '0.5rem 0.75rem',
              background: 'rgba(37, 99, 235, 0.08)',
              border: '1px solid rgba(37, 99, 235, 0.3)',
              borderRadius: '6px',
              fontSize: '0.8125rem',
              marginBottom: '0.75rem',
              color: 'var(--text-primary)',
            }}>
              Backdated check-in — the entered date/time ({checkInDate} {checkInTime}) will be
              recorded as the actual check-in (after-midnight arrival or migration entry).
            </div>
          )}

          {/* ── Arrival changes — MEAL PLAN only. Rooms, pax and the agreed
                 rate are shown for context but are read-only: they are the
                 booking's commercial figures and belong to Modify Stay, which
                 re-prices properly and routes a materially worse change to
                 approval. This is the ONE entry point for the panel (the old
                 second trigger in Billing was removed — two doors onto one
                 action left the operator unsure which was authoritative). ── */}
          {/* Hidden in amend mode: the server only accepts arrival changes on a
              CONFIRMED booking — post-check-in changes go through the in-house
              flows (Phase B: FO change requests). */}
          {canChangeArrival && !isAmend && !arrivalOpen && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setArrivalOpen(true)}
              disabled={submitting}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem' }}
            >
              Arrival changes…
            </button>
          )}
          {canChangeArrival && !isAmend && arrivalOpen && (
            <div style={{
              border: '1px solid rgba(37, 99, 235, 0.35)', background: 'rgba(37, 99, 235, 0.05)',
              borderRadius: '8px', padding: '0.6rem 0.75rem', marginBottom: '0.875rem',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem' }}>
                <strong style={{ fontSize: '0.8125rem' }}>Arrival changes</strong>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                  Meal plan only · rooms, pax and rate change via Modify Stay
                </span>
              </div>

              {/* Column headings. The row was four unlabelled number boxes, so the
                  operator had to guess which was rooms, adults, children or rate —
                  exactly the kind of guess that produces a wrong arrival change. */}
              <div className="arr-head">
                <span>Category</span>
                <span className="n">Rooms</span>
                <span className="n">Adults</span>
                <span className="n">Child</span>
                <span>Meal plan</span>
                <span className="n">Agreed rate</span>
                <span>Reason / remark</span>
              </div>

              {/* Shared style for the read-only commercial cells. */}
              {detailLines.map((d) => {
                const roText: React.CSSProperties = {
                  fontSize: '0.75rem', textAlign: 'right', color: 'var(--text-secondary)',
                  fontVariantNumeric: 'tabular-nums', padding: '0.2rem 0.35rem',
                };
                const edit = arrivalEdits[d.bookingDetailId] ?? { remarks: '' };
                const patch = (p: Partial<typeof edit>) =>
                  setArrivalEdits((prev) => ({ ...prev, [d.bookingDetailId]: { ...edit, ...p } }));

                return (
                  <div key={d.bookingDetailId} style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0,1.15fr) 52px 52px 52px 84px minmax(0,0.85fr) minmax(0,1.15fr)',
                    gap: '0.4rem', alignItems: 'center', marginBottom: '0.35rem', fontSize: '0.75rem',
                  }}>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {d.categoryName || d.categoryCode}
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem' }}>
                        {' '}· rack {(d.ratePerRoom ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </span>
                    {/* Rooms, pax and rate are READ-ONLY here. They are the
                        booking's commercial figures, and editing them from the
                        check-in screen was a second door onto the same fact —
                        it moved BOOKED pax while Occupancy held ACTUAL pax,
                        with nothing reconciling the two. Changing any of them
                        now goes through Modify Stay, which re-prices properly. */}
                    <span style={roText} title="Rooms booked — change via Modify Stay">
                      {d.numberOfRooms ?? '—'}
                    </span>
                    <span style={roText} title="Adults booked — actual arrivals go in Occupancy">
                      {d.numAdults ?? '—'}
                    </span>
                    <span style={roText} title="Children booked — actual arrivals go in Occupancy">
                      {d.numChildren ?? 0}
                    </span>
                    {/* Meal plan can move at arrival — the kitchen and the price
                        both follow it, and the server already accepts it. */}
                    <select
                      className="form-input"
                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.25rem' }}
                      value={edit.mealPlanId ?? ''}
                      onChange={(e) => patch({ mealPlanId: e.target.value })}
                      disabled={applyingArrival || submitting}
                      title="Meal plan taken on arrival"
                    >
                      <option value="">{d.mealPlanCode || 'Plan'}</option>
                      {arrivalMealPlans.map((m) => (
                        <option key={m.id} value={m.id}>{m.planCode}</option>
                      ))}
                    </select>
                    <span style={roText} title="Agreed rate — change via Modify Stay">
                      {(d.offeredRatePerRoom ?? d.ratePerRoom ?? 0)
                        .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <input
                      type="text" className="form-input" maxLength={200}
                      style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem' }}
                      placeholder="Why the plan changed…"
                      value={edit.remarks}
                      onChange={(e) => patch({ remarks: e.target.value })}
                      disabled={applyingArrival || submitting}
                    />
                  </div>
                );
              })}

              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.5rem' }}>
                <button
                  type="button" className="btn btn-primary"
                  style={{ fontSize: '0.75rem', padding: '0.3rem 0.7rem' }}
                  onClick={handleApplyArrivalChanges}
                  disabled={applyingArrival || submitting}
                >
                  {applyingArrival ? 'Applying…' : 'Apply arrival changes'}
                </button>
                <button
                  type="button" className="btn btn-secondary"
                  style={{ fontSize: '0.75rem', padding: '0.3rem 0.7rem' }}
                  onClick={() => { setArrivalEdits({}); setArrivalOpen(false); }}
                  disabled={applyingArrival}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      ),
    },

    // ── ② Guests & Occupancy — who is on the stay, plus the headcount.
    //      Merged (Aug 2026 redesign): actual-pax-on-site and the gender-split
    //      fallback are the same mental task as the guest list, and the
    //      pax-mismatch warning belongs next to the counters it contradicts. ──
    {
      id: 'guests',
      title: 'Guests & Occupancy',
      badge: { done: namedGuestRows.length, total: Math.max(guestRows.length, 1) },
      errorCount: errorCounts['guests'],
      content: (
        <>
          {/* ── Guests (D3 Phase A) — shared panel used by Quick Check-In and
                 the Walk-In desk sheet. Pax-mismatch soft warning rides in as
                 headerExtra with the arrival-changes one-tap fix. ── */}
          <CheckInGuestsPanel
            guestRows={guestRows}
            onUpdateRow={updateGuestRow}
            onSetPrimary={setPrimaryGuest}
            onAddRow={addGuestRow}
            onRemoveRow={removeGuestRow}
            fieldErrors={fieldErrors}
            submitting={submitting}
            requireAllGuestDetails={requireAllGuestDetails}
            bookedPax={bookedPax}
            headerExtra={paxMismatch ? (
              <div style={{
                padding: '0.4rem 0.65rem', borderRadius: '6px', fontSize: '0.78rem', marginBottom: '0.375rem',
                background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.35)',
                color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap',
              }}>
                <span>
                  {namedGuestCount} guest{namedGuestCount !== 1 ? 's' : ''} listed but the booking has {bookedPax} pax.
                </span>
                {/* Fixing the pax now happens in place, under the arrival-changes
                    permission — the link jumps to the Arrival section where the
                    panel lives. Without the permission this stays an
                    informational warning rather than a button that would 403. */}
                {canChangeArrival ? (
                  <button
                    type="button"
                    onClick={() => { setArrivalOpen(true); setActiveSection('arrival'); }}
                    disabled={submitting}
                    style={{
                      border: 'none', background: 'none', cursor: 'pointer', padding: 0,
                      fontSize: '0.78rem', fontWeight: 600, color: '#2563eb',
                    }}
                  >
                    Update pax for this arrival…
                  </button>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>
                    Contact Reservations to amend the booking.
                  </span>
                )}
              </div>
            ) : undefined}
          />

          {/* ── Occupancy — actual pax on site (FO planning). Defaults to the
                 booked pax; follows the named guest rows when the property
                 requires all guest details. Infants exist ONLY here. ── */}
          <div style={{
            marginTop: '1rem', paddingTop: '0.75rem',
            borderTop: '1px solid var(--border-color)',
          }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Occupancy — actual pax on site
            </div>
            <div className="form-grid-3" style={{ marginBottom: '0.875rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  Adults on site
                  {bk.numAdults != null && (
                    <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (booked {bk.numAdults})</span>
                  )}
                </label>
                <input
                  type="number" min={0} className="form-input" value={numAdultsIn}
                  onChange={(e) => { paxTouchedRef.current = true; setNumAdultsIn(e.target.value); }}
                  disabled={submitting}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  Children
                  {bk.numChildren != null && (
                    <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (booked {bk.numChildren})</span>
                  )}
                </label>
                <input
                  type="number" min={0} className="form-input" value={numChildrenIn}
                  onChange={(e) => { paxTouchedRef.current = true; setNumChildrenIn(e.target.value); }}
                  disabled={submitting}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Infants</label>
                <input
                  type="number" min={0} className="form-input" value={numInfantsIn}
                  onChange={(e) => { paxTouchedRef.current = true; setNumInfantsIn(e.target.value); }}
                  disabled={submitting}
                />
              </div>
            </div>

            {/* ── Over-occupancy advisory + override reason (tables/082).
                   Allowed, never a hard block — a hard block pushes staff to
                   under-report pax, destroying the number the rule protects.
                   The reason is stored on tbl_checkin for compliance. ── */}
            {overOccupancy && (
              <div style={{ marginBottom: '0.875rem' }}>
                <div style={{
                  fontSize: '0.75rem', border: '1px solid rgba(217, 119, 6, 0.4)',
                  background: 'rgba(217, 119, 6, 0.08)', borderRadius: '6px',
                  padding: '0.5rem 0.75rem', marginBottom: '0.5rem',
                  color: 'var(--text-primary)',
                }}>
                  ⚠ {actualPaxOnSite} guests exceed the maximum occupancy of {occupancyCapacity} for
                  the booked rooms. Allowed — record a reason to proceed (kept on the
                  registration record for compliance).
                </div>
                <div className="form-group" style={{ marginBottom: 0 }} data-field="occupancyOverrideReason">
                  <label className="form-label">
                    Over-occupancy reason <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text" maxLength={200}
                    className={`form-input${fieldErrors.occupancyOverrideReason ? ' form-field-error' : ''}`}
                    value={occupancyOverrideReason}
                    placeholder="e.g. Extra mattress provided — family with children"
                    onChange={(e) => {
                      clearFieldError('occupancyOverrideReason');
                      setOccupancyOverrideReason(e.target.value);
                    }}
                    disabled={submitting}
                  />
                  {fieldErrors.occupancyOverrideReason && (
                    <div className="form-error-message">{fieldErrors.occupancyOverrideReason}</div>
                  )}
                </div>
              </div>
            )}

            {/* ── MAP meal preference (buffet check-off slice 1). Only for a stay
                   on an either/or plan, and only at check-in: later changes are
                   made from In-House Guests → Meals, so an open Edit Check-In can
                   never overwrite a newer choice. It predicts; the guest still
                   chooses on the day (unless the plan is fixed). ── */}
            {!isAmend && hasMajorMealChoice && (
              <div className="form-grid-2" style={{ marginBottom: '0.875rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }} data-field="majorMealPref">
                  <label className="form-label">MAP meal the guests expect</label>
                  <select
                    className="form-input"
                    value={majorMealPref}
                    onChange={(e) => setMajorMealPref(e.target.value as '' | 'LUNCH' | 'DINNER' | 'EITHER')}
                    disabled={submitting}
                  >
                    <option value="">
                      Plan default ({majorMealPlan?.defaultMajorMeal === 'LUNCH' ? 'Lunch' : 'Dinner'})
                    </option>
                    <option value="LUNCH">Lunch</option>
                    <option value="DINNER">Dinner</option>
                    <option value="EITHER">Either — not sure yet</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    Ask which meal they usually take. It helps the kitchen plan and the
                    restaurant follow up; it can be changed day by day from In-House Guests.
                  </span>
                </div>
              </div>
            )}

            {/* ── Fallback gender split — visible only while the guest rows
                   don't carry a gender for every named adult (decision 8). ── */}
            {showGenderFallback && (
              <div className="form-grid-2">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">
                    Adults — Male
                    {bk.numAdults != null && (
                      <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (of {bk.numAdults})</span>
                    )}
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="form-input"
                    value={numMale}
                    onChange={(e) => setNumMale(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Adults — Female</label>
                  <input
                    type="number"
                    min={0}
                    className="form-input"
                    value={numFemale}
                    onChange={(e) => setNumFemale(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      ),
    },

    // ── ③ GRC Details — the registration-card narrative. Remarks folded in
    //      (Aug 2026 redesign): both are card prose, and a lone textarea was
    //      not worth a nav entry. ──
    {
      id: 'grc',
      title: 'GRC Details',
      badge: { done: grcFilled, total: 3 },
      errorCount: errorCounts['grc'],
      content: (
        <>
          {/* Mobile and email live on the guest rows now — contact details
              belong to a person, not a stay. The card still snapshots the
              primary guest's, derived at save. */}
          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea
              className="form-textarea"
              value={grcAddress}
              onChange={(e) => setGrcAddress(e.target.value)}
              rows={2}
              placeholder="Address as declared by the guest"
              style={{ resize: 'vertical', minHeight: '44px' }}
              disabled={submitting}
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect
                label="Purpose of Visit"
                options={purposeOptions.map((o) => ({ value: o, label: o }))}
                value={purposeOfVisit}
                onChange={(v) => setPurposeOfVisit((v as string) || '')}
                isClearable={false}
                isDisabled={submitting}
                size="sm"
                menuPortalTarget={document.body}
                placeholder="Select purpose…"
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                Proceeding To {requireNextDestination && <span className="text-danger">*</span>}
              </label>
              <input
                type="text"
                className={`form-input${blockNextDest ? ' form-field-error' : ''}`}
                value={nextDestination}
                onChange={(e) => setNextDestination(e.target.value)}
                maxLength={150}
                placeholder="Next destination"
                disabled={submitting}
              />
              {blockNextDest && <div className="form-error-message">Required by this property</div>}
            </div>
          </div>

          {/* Only meaningful against "Other" — a note left against a listed
              purpose would be a stale sentence contradicting the code. */}
          {purposeOfVisit.trim().toLowerCase() === 'other' && (
            <div className="form-group">
              <label className="form-label">Please specify</label>
              <input
                type="text"
                className="form-input"
                value={purposeNote}
                onChange={(e) => setPurposeNote(e.target.value)}
                maxLength={100}
                placeholder="e.g. Film shoot — 4 day crew stay"
                disabled={submitting}
              />
            </div>
          )}

          {/* Scan of the card the guest signed (GRC-4): one per stay, kept apart from ID documents. */}
          {isAmend && amendRecord && (
            <div className="form-group">
              <label className="form-label" htmlFor="chk-signed-card">Signed registration card</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.82rem' }}>
                <span style={{ color: amendRecord.hasSignedCard ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                  {amendRecord.hasSignedCard
                    ? `On file${amendRecord.grcScanByName ? ` · ${amendRecord.grcScanByName}` : ''}${amendRecord.grcScanAt
                      ? `, ${new Date(amendRecord.grcScanAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}`
                    : 'Not uploaded yet'}
                </span>
                {amendRecord.hasSignedCard && (
                  <button type="button" className="btn btn-secondary btn-sm" disabled={submitting || signedBusy}
                    onClick={() => { void viewSignedCard(); }}>
                    View
                  </button>
                )}
                <button type="button" className="btn btn-secondary btn-sm" disabled={submitting || signedBusy}
                  onClick={() => signedInput.current?.click()}>
                  {signedBusy ? 'Uploading…' : amendRecord.hasSignedCard ? 'Replace…' : 'Upload…'}
                </button>
                <input ref={signedInput} id="chk-signed-card" type="file" hidden accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void uploadSignedCard(f); }} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                PDF or photo of the card after the guest has filled and signed it. Not an ID document.
              </div>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Remarks</label>
            <textarea
              className="form-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={200}
              placeholder="Optional — anything the next shift should know?"
              style={{ resize: 'vertical', minHeight: '50px' }}
              disabled={submitting}
            />
          </div>
        </>
      ),
    },

    // ── ④ Rooms — the assignment board, now full width. Auto-assign runs on
    //      open, so this section usually starts complete; the Board view is
    //      the main beneficiary of the extra room. ──
    {
      id: 'rooms',
      title: 'Rooms',
      badge: isAmend
        ? 'ok'
        : assignments.length > 0
          ? { done: assignedCount, total: assignments.length }
          : undefined,
      errorCount: errorCounts['rooms'],
      content: isAmend ? (
        // ── Amend mode: rooms are LOCKED. Room Move (Room Board) owns physical
        // moves — a second editing path here is how boards go stale. ──
        <>
          <div style={{
            padding: '0.5rem 0.75rem', marginBottom: '0.6rem',
            background: 'var(--bg-secondary)', border: '1px dashed var(--border-color)',
            borderRadius: '6px', fontSize: '0.78rem', color: 'var(--text-secondary)',
          }}>
            Room assignments are locked here — use <b>Room Move</b> on the Room Board to
            relocate a guest.
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {(amendRecord?.rooms ?? []).map((r) => (
              <span
                key={r.roomId}
                style={{
                  border: '1px solid var(--form-border-color)', borderRadius: '6px',
                  padding: '0.3rem 0.6rem', fontSize: '0.8125rem', fontWeight: 600,
                  color: 'var(--text-primary)', background: 'var(--bg-primary)',
                }}
              >
                {r.roomNumber}
                {r.categoryName && (
                  <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · {r.categoryName}</span>
                )}
              </span>
            ))}
            {(amendRecord?.rooms ?? []).length === 0 && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                Loading assigned rooms…
              </span>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'space-between', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {assignedCount} / {assignments.length} assigned
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              {/* List ⇄ Board view toggle — same picks, two ways to make them */}
              <div style={{ display: 'inline-flex', border: '1px solid var(--form-border-color)', borderRadius: '6px', overflow: 'hidden' }}>
                {(['list', 'board'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setRoomViewMode(mode)}
                    disabled={submitting}
                    title={mode === 'list' ? 'List view — one picker per slot' : 'Room board — tap free rooms'}
                    style={{
                      padding: '0.25rem 0.55rem', fontSize: '0.72rem', fontWeight: 600,
                      border: 'none', cursor: 'pointer',
                      background: roomViewMode === mode ? '#2563eb' : 'transparent',
                      color: roomViewMode === mode ? '#fff' : 'var(--text-secondary)',
                      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    }}
                  >
                    {mode === 'list' ? <IconSystem.list size={13} /> : <IconSystem.grid size={13} />}
                    {mode === 'list' ? 'List' : 'Board'}
                  </button>
                ))}
              </div>
              {assignments.some((a) => !a.roomId) && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAutoAssign}
                  disabled={submitting || autoAssigning}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
                  title="Fill every empty slot with the first free room of its category (made-up rooms first)"
                >
                  {autoAssigning ? 'Assigning…' : 'Auto-assign remaining'}
                </button>
              )}
            </div>
          </div>
          {!loadingBooking && detailLines.length === 0 && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              No booked rooms to assign.
            </div>
          )}
          {/* Partial arrival (BR-CHKIN-002 revised) — the unassigned rooms must
              be explicitly confirmed as NO-SHOW: assigned + no-show = booked,
              never an ambiguous hold. Expecting the guest later? Allocate the
              room and keep the key ready instead. */}
          {!isAmend && unassignedRooms > 0 && assignments.some((a) => a.roomId) && (
            <div style={{
              padding: '0.5rem 0.65rem', borderRadius: '6px', fontSize: '0.75rem',
              marginBottom: '0.5rem', background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.45)', color: 'var(--text-primary)',
            }}>
              <b>Partial arrival</b> — {unassignedRooms} of {assignments.length} room
              {assignments.length !== 1 ? 's' : ''} not assigned:{' '}
              {Object.entries(
                assignments.filter((a) => !a.roomId).reduce<Record<string, number>>((acc, a) => {
                  const cat = a.bookedCategoryName || 'Room';
                  acc[cat] = (acc[cat] ?? 0) + 1;
                  return acc;
                }, {}),
              ).map(([cat, n]) => `${n}× ${cat}`).join(', ')}.
              {' '}Confirm them as <b>No-Show</b> to save — their remaining nights go back on
              sale immediately and booking staff decide the charge per the no-show policy.
              Expecting the guest later? Assign the room and keep the key ready instead.
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.45rem', cursor: 'pointer', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={noShowConfirmed}
                  onChange={(e) => {
                    setNoShowConfirmed(e.target.checked);
                    setFieldErrors((prev) => { const n = { ...prev }; delete n.noShowConfirm; return n; });
                  }}
                />
                Mark {unassignedRooms} unassigned room{unassignedRooms !== 1 ? 's' : ''} as No-Show
              </label>
              {fieldErrors.noShowConfirm && (
                <div className="form-error-message">{fieldErrors.noShowConfirm}</div>
              )}
              {noShowConfirmed && (
                <div style={{ marginTop: '0.4rem' }}>
                  <textarea
                    className={`form-input form-textarea${fieldErrors.noShowRemarks ? ' form-field-error' : ''}`}
                    rows={2} maxLength={500}
                    value={noShowRemarks}
                    onChange={(e) => {
                      setNoShowRemarks(e.target.value);
                      setFieldErrors((prev) => { const n = { ...prev }; delete n.noShowRemarks; return n; });
                    }}
                    placeholder="No-show remark (required) — the desk's account; booking staff price from this…"
                    style={{ fontSize: '0.75rem' }}
                  />
                  {fieldErrors.noShowRemarks && (
                    <div className="form-error-message">{fieldErrors.noShowRemarks}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* The section pane itself scrolls — no nested scroll areas. */}
          {roomViewMode === 'list' && (
          <div>
          {assignments.map((row, idx) => {
            const isChanged = row.roomCategoryId !== row.bookedCategoryId;
            return (
              <div
                key={row.rowKey}
                style={{
                  marginBottom: '0.5rem', padding: '0.4rem 0.5rem', borderRadius: '6px',
                  border: `1px solid ${isChanged ? 'rgba(245, 158, 11, 0.45)' : 'var(--border-color)'}`,
                  background: isChanged ? 'rgba(245, 158, 11, 0.06)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.3rem', fontSize: '0.78rem' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Room #{idx + 1}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    Booked: <strong style={{ color: 'var(--text-primary)' }}>{row.bookedCategoryName || '—'}</strong>
                  </span>
                  {isChanged && (() => {
                    const kind = classifyChange(row.bookedCategoryId, row.roomCategoryId);
                    const down = kind === 'downgrade';
                    return (
                      <span style={{
                        padding: '0.05rem 0.45rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700,
                        ...(down
                          ? { background: 'rgba(185, 28, 28, 0.12)', color: '#b91c1c', border: '1px solid rgba(185, 28, 28, 0.4)' }
                          : { background: 'rgba(245, 158, 11, 0.15)', color: '#92400e', border: '1px solid rgba(245, 158, 11, 0.4)' }),
                      }}>
                        {down ? '↓ DOWNGRADE to ' : '→ '}{row.categoryName || 'other category'}
                      </span>
                    );
                  })()}
                  {canChangeCategory && (
                    <button
                      type="button"
                      onClick={() => {
                        setChangeOpenFor((prev) => (prev === row.rowKey ? null : row.rowKey));
                        ensureCategoryCounts();
                      }}
                      disabled={submitting}
                      style={{
                        marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: '0.72rem', fontWeight: 600, color: '#2563eb', padding: 0,
                      }}
                    >
                      {changeOpenFor === row.rowKey ? 'Close' : isChanged ? 'Change category…' : 'Other category…'}
                    </button>
                  )}
                </div>
                {changeOpenFor === row.rowKey && (
                  <div style={{ marginBottom: '0.35rem' }}>
                    <SyutSelect
                      options={categoryList.map((c) => {
                        // Nights of the stay this category would be short if this
                        // slot moved into it. The "available" count beside it is
                        // TONIGHT's free rooms — a category can look wide open
                        // tonight and be committed solid later in the stay, which
                        // is exactly the trap this chip exists to close.
                        const shortNights = shortNightsForCategory(
                          inventoryImpact,
                          c.id,
                          (roomsByCategory[c.id] ?? 0) + (row.roomCategoryId === c.id ? 0 : 1),
                        );
                        return {
                          value: c.id,
                          label: `${c.name}${c.id === row.bookedCategoryId ? ' (booked category)' : ''}${
                            categoryCounts[c.id] !== undefined ? ` — ${categoryCounts[c.id]} available` : ''}${
                            shortNights > 0 ? ` ⚠ short ${shortNights} night${shortNights === 1 ? '' : 's'}` : ''}`,
                        };
                      })}
                      value={row.roomCategoryId}
                      onChange={(v) => handleCategoryChange(row.rowKey, (v as string) || null)}
                      placeholder="Assign from category…"
                      size="sm"
                      menuPortalTarget={document.body}
                    />
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                      Guest pays the {row.bookedCategoryName} rate regardless — inventory and reports reconcile automatically.
                    </div>
                  </div>
                )}
                <SyutSelect
                  key={`${row.rowKey}-${row.roomCategoryId}`}
                  isAsync
                  loadOptions={buildLoadOptions(row.rowKey, row.roomCategoryId)}
                  defaultOptions={true}
                  cacheOptions={false}
                  value={row.roomId}
                  onChange={(v) => handleAssignmentChange(row.rowKey, v)}
                  placeholder="Pick a room…"
                  size="sm"
                  error={fieldErrors[`assignment-${row.rowKey}`]}
                  isDisabled={submitting}
                  menuPortalTarget={document.body}
                />
              </div>
            );
          })}
          </div>
          )}{/* /list view */}

          {/* ── BOARD view — Walk-In-style tappable free-room chips ── */}
          {roomViewMode === 'board' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {Object.keys(fieldErrors).some((k) => k.startsWith('assignment-')) && (
              <div className="form-error-message">
                Pick a room for every slot — {assignments.filter((a) => !a.roomId).length} still empty.
              </div>
            )}
            {/* Room-number filter — only worth showing once the board carries
                more than a couple of categories. */}
            {categoryList.length > 2 && (
              <input
                type="text"
                className="form-input"
                value={roomFilter}
                onChange={(e) => setRoomFilter(e.target.value)}
                placeholder="Filter by room number…"
                disabled={submitting}
                style={{ fontSize: '0.78rem', padding: '0.25rem 0.5rem' }}
              />
            )}
            {boardCategories.map((cat) => {
              const rep = assignments.find((a) => a.roomCategoryId === cat.id);
              const groupRows = assignments.filter((a) => a.roomCategoryId === cat.id);
              const groupAssigned = groupRows.filter((a) => a.roomId).length;
              const allRooms = boardLists[cat.id] ?? [];
              const rooms = roomFilter.trim()
                ? allRooms.filter((r) => r.roomNumber.toLowerCase().includes(roomFilter.trim().toLowerCase()))
                : allRooms;
              // "Booked" = a slot was originally booked into this category.
              const isBookedCat = assignments.some((a) => a.bookedCategoryId === cat.id);
              const isOpen = isBookedCat || !!openCategories[cat.id] || !!roomFilter.trim();
              const bookedRef = assignments[0]?.bookedCategoryId ?? '';
              const kind = isBookedCat ? 'same' : classifyChange(bookedRef, cat.id);
              const isChangedGroup = !!rep && rep.roomCategoryId !== rep.bookedCategoryId;

              // Collapsed non-booked category: one line, one click to open.
              if (!isOpen) {
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      // The board-list effect picks this up and fetches; doing
                      // it here as well produced a second, divergent path.
                      setOpenCategories((prev) => ({ ...prev, [cat.id]: true }));
                    }}
                    disabled={submitting}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      width: '100%', textAlign: 'left', cursor: 'pointer',
                      border: '1px dashed var(--border-color)', borderRadius: '6px',
                      padding: '0.35rem 0.55rem', background: 'transparent',
                      fontSize: '0.78rem', color: 'var(--text-secondary)',
                    }}
                  >
                    <span>
                      ▸ {cat.name}
                      {kind === 'upgrade' && <span style={{ color: '#047857', marginLeft: '0.35rem' }}>upgrade</span>}
                      {kind === 'downgrade' && <span style={{ color: '#b91c1c', marginLeft: '0.35rem' }}>downgrade</span>}
                    </span>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {categoryCounts[cat.id] !== undefined ? `${categoryCounts[cat.id]} free` : 'view rooms'}
                    </span>
                  </button>
                );
              }

              return (
                <div key={cat.id} style={{
                  border: `1px solid ${groupAssigned ? 'rgba(37, 99, 235, 0.45)' : 'var(--border-color)'}`,
                  borderRadius: '6px', padding: '0.45rem 0.55rem',
                  background: groupAssigned ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                    <strong style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
                      {cat.name}
                    </strong>
                    {isChangedGroup && rep && (
                      <span style={{
                        padding: '0.05rem 0.45rem', borderRadius: '999px', fontSize: '0.68rem', fontWeight: 700,
                        ...(kind === 'downgrade'
                          ? { background: 'rgba(185, 28, 28, 0.12)', color: '#b91c1c', border: '1px solid rgba(185, 28, 28, 0.4)' }
                          : { background: 'rgba(245, 158, 11, 0.15)', color: '#92400e', border: '1px solid rgba(245, 158, 11, 0.4)' }),
                      }}>
                        {kind === 'downgrade' ? 'DOWNGRADE from ' : 'hosting '}{rep.bookedCategoryName}
                      </span>
                    )}
                    {!isBookedCat && groupRows.length === 0 && (
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                        {kind === 'downgrade' ? 'lower category' : kind === 'upgrade' ? 'upgrade' : 'other category'}
                      </span>
                    )}
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      {groupRows.length > 0 ? `${groupAssigned} / ${groupRows.length} assigned` : `${rooms.length} free`}
                    </span>
                    {!isBookedCat && (
                      <button
                        type="button"
                        onClick={() => setOpenCategories((prev) => ({ ...prev, [cat.id]: false }))}
                        disabled={submitting}
                        style={{
                          marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer',
                          fontSize: '0.7rem', color: 'var(--text-muted)', padding: 0,
                        }}
                      >
                        collapse
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {/* undefined = not fetched yet; [] = fetched and none free.
                        Conflating them left an empty category claiming to load
                        for ever. */}
                    {boardLists[cat.id] === undefined && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Loading rooms…</span>
                    )}
                    {boardLists[cat.id] !== undefined && rooms.length === 0 && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {roomFilter.trim()
                          ? `No room matches "${roomFilter.trim()}" here.`
                          : 'No free rooms in this category for these dates.'}
                      </span>
                    )}
                    {rooms.map((room) => {
                      const isSelected = assignments.some((a) => a.roomId === room.roomId);
                      const isDirtyRoom = room.housekeepingStatusCode === 'ROOM_DIRTY';
                      return (
                        <button
                          key={room.roomId}
                          type="button"
                          onClick={() => handleBoardRoomTap(cat.id, room)}
                          disabled={submitting}
                          title={`${room.roomNumber}${room.floorNumber ? ` · Floor ${room.floorNumber}` : ''}${room.housekeepingStatusName ? ` · ${room.housekeepingStatusName}` : ''}`}
                          style={{
                            padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600,
                            cursor: 'pointer',
                            border: `1.5px solid ${isSelected ? '#2563eb' : isDirtyRoom ? 'rgba(245, 158, 11, 0.6)' : 'var(--form-border-color)'}`,
                            background: isSelected ? '#2563eb' : 'transparent',
                            color: isSelected ? '#fff' : 'var(--text-primary)',
                          }}
                        >
                          {room.roomNumber}{isDirtyRoom ? ' ⚠' : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          )}{/* /board view */}
          {dirtyAssignedRooms.length > 0 && (
            <div style={{
              padding: '0.45rem 0.65rem', borderRadius: '6px', fontSize: '0.78rem', marginTop: '0.25rem',
              background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.35)',
              color: 'var(--text-primary)',
            }}>
              ⚠ Room{dirtyAssignedRooms.length !== 1 ? 's' : ''}{' '}
              {dirtyAssignedRooms.map((r) => r.roomNumber).join(', ')} {dirtyAssignedRooms.length !== 1 ? 'are' : 'is'} marked
              Dirty — make sure housekeeping finishes before the guest goes up.
            </div>
          )}
        </>
      ),
    },

    // ── ⑤ Billing & Payment — mostly review on check-in: what was booked,
    //      who owns the booking, and the payment state. The balance also rides
    //      in the summary strip so it is never out of sight. ──
    {
      id: 'billing',
      title: 'Billing & Payment',
      badge: balanceAmt > 0 ? 'warn' : 'ok',
      errorCount: errorCounts['billing'],
      content: (
        <>
          {/* ── What was booked — reads as the invoice will: category, rooms,
                 nights, rate and plan, or pax x rate for a per-pax group. ── */}
          {detailLines.length > 0 && (
            <div className="chk-lines">
              {detailLines.map((d) => {
                const perPax = d.rateBasis === 'PER_PAX';
                const pax = (d.numAdults ?? 0) + (d.numChildren ?? 0);
                const nights = bk.numNights ?? 1;
                const nightLabel = `${nights} night${nights !== 1 ? 's' : ''}`;
                return (
                  <div className="chk-line" key={d.bookingDetailId}>
                    <span className="chk-line-what">
                      <b>{d.categoryName || d.categoryCode}</b>
                      {perPax
                        ? ` · ${pax} pax`
                        : ` × ${d.numberOfRooms} room${d.numberOfRooms !== 1 ? 's' : ''}`}
                      {d.allocatedCategoryName && d.allocatedCategoryName !== d.categoryName && (
                        <span className="chk-line-upg"> → {d.allocatedCategoryName}</span>
                      )}
                    </span>
                    <span className="chk-line-rate">
                      {nightLabel} @ {formatCurrency(
                        perPax ? (d.ratePerPax ?? 0) : (d.offeredRatePerRoom ?? d.ratePerRoom ?? 0),
                      )}
                      {perPax ? '/pax' : '/room'}
                      {d.mealPlanCode && <span className="chk-line-plan"> · {d.mealPlanCode}</span>}
                    </span>
                    <span className="chk-line-amt">
                      {formatCurrency(d.sellingLineTotal ?? d.lineTotal ?? 0)}
                    </span>
                  </div>
                );
              })}

              {/* Reconciliation to the payable figure. The lines above are what
                  was SOLD; the booking discount is a header-level fact, so it is
                  shown as its own row rather than silently spread into the line
                  amounts — 10,000 of lines beside a 6,600 total with nothing
                  explaining the gap is how this card read before. */}
              {(() => {
                const lineSum = detailLines.reduce(
                  (a, d) => a + (d.sellingLineTotal ?? d.lineTotal ?? 0), 0);
                const discount = bk.discountAmount ?? 0;
                const addons = bk.addonTotal ?? 0;
                const net = bk.finalGrandTotal ?? bk.grandTotal ?? Math.max(0, lineSum - discount + addons);
                if (discount <= 0 && addons <= 0) return null;
                return (
                  <>
                    <div className="chk-line chk-line-calc">
                      <span className="chk-line-what">Subtotal</span>
                      <span />
                      <span className="chk-line-amt" style={{ fontWeight: 500 }}>{formatCurrency(lineSum)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="chk-line chk-line-calc">
                        <span className="chk-line-what" style={{ color: '#b45309' }}>
                          Discount{bk.discountType === 'Percentage' && bk.discountValue
                            ? ` (${bk.discountValue}%)` : ''}
                        </span>
                        <span />
                        <span className="chk-line-amt" style={{ color: '#b45309' }}>
                          −{formatCurrency(discount)}
                        </span>
                      </div>
                    )}
                    {addons > 0 && (
                      <div className="chk-line chk-line-calc">
                        <span className="chk-line-what">Add-ons</span>
                        <span />
                        <span className="chk-line-amt" style={{ fontWeight: 500 }}>+{formatCurrency(addons)}</span>
                      </div>
                    )}
                    <div className="chk-line chk-line-net">
                      <span className="chk-line-what"><b>Net total</b></span>
                      <span />
                      <span className="chk-line-amt">{formatCurrency(net)}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ── Who owns this booking (decision 10) — the desk needs to know
                 which channel to call, and which executive committed what. ── */}
          <dl className="chk-src">
            <dt>Source</dt>
            <dd>
              {bk.bookingSource || '—'}
              {(bk.agentName || bk.corporateName || bk.otaName)
                && ` · ${bk.agentName || bk.corporateName || bk.otaName}`}
            </dd>
            {bk.createdByName && (
              <>
                <dt>Booked by</dt>
                <dd>{bk.createdByName}</dd>
              </>
            )}
          </dl>
          {/* ── Payment Status (BR-CHKIN-005) — always visible so the operator
                 sees the full financial state, not just a balance warning. ── */}
          <div
            style={{
              padding: '0.5rem 0.75rem',
              background: balanceAmt > 0 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.08)',
              border: `1px solid ${balanceAmt > 0 ? 'rgba(245, 158, 11, 0.35)' : 'rgba(16, 185, 129, 0.35)'}`,
              borderRadius: '6px',
              fontSize: '0.8125rem',
              marginBottom: '0.875rem',
              color: 'var(--text-primary)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.125rem',
            }}
          >
            {/* 3-column grid so the three amounts sit at the same fixed
                position across renders and look equally weighted, instead
                of bunching on the left with empty space on the right. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
              <span>
                <span style={{ color: 'var(--text-secondary)' }}>Total:</span>{' '}
                <strong>{fmtAmt(totalAmt)}</strong>
              </span>
              <span>
                <span style={{ color: 'var(--text-secondary)' }}>Collected:</span>{' '}
                <strong>{fmtAmt(collectedAmt)}</strong>
              </span>
              <span>
                <span style={{ color: 'var(--text-secondary)' }}>Balance:</span>{' '}
                <strong style={{ color: balanceAmt > 0 ? '#b45309' : '#047857' }}>
                  {fmtAmt(balanceAmt)}
                </strong>
              </span>
            </div>
            {balanceAmt > 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                Outstanding balance pending — proceed with check-in?
              </div>
            )}
            <div style={{ marginTop: '0.35rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setPayOpen((o) => !o)}
                disabled={submitting}
                style={{ fontSize: '0.72rem', padding: '0.2rem 0.6rem' }}
              >
                {payOpen ? 'Hide payment' : 'Collect payment'}
              </button>
            </div>
          </div>

          {/* Collect-payment panel — in place, not a nested modal (nested modals
              were removed from this screen when Arrival Changes moved in-place).
              The amount is deliberately NOT pre-filled: guests part-pay, round
              off, or split cash and card, and a receipt that does not match what
              was handed over is worse than a blank field. */}
          {payOpen && (
            <div
              style={{
                padding: '0.6rem 0.7rem',
                marginBottom: '0.875rem',
                border: '1px solid rgba(37, 99, 235, 0.35)',
                borderRadius: '6px',
                background: 'rgba(37, 99, 235, 0.06)',
              }}
            >
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary)', marginBottom: '0.45rem' }}>
                Collect payment
              </div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Amount received <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    className="form-input"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    placeholder="Enter amount…"
                    disabled={payPosting || submitting}
                  />
                </div>
                <div className="form-group">
                  <SyutSelect
                    label="Mode"
                    options={[
                      { value: 'Cash', label: 'Cash' },
                      { value: 'Card', label: 'Card' },
                      { value: 'UPI', label: 'UPI' },
                      { value: 'Bank Transfer', label: 'Bank Transfer' },
                    ]}
                    value={payMode}
                    onChange={(v) => setPayMode((v as string) || 'Cash')}
                    isClearable={false}
                    isDisabled={payPosting || submitting}
                    size="sm"
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Reference</label>
                  <input
                    type="text"
                    className="form-input"
                    value={payReference}
                    onChange={(e) => setPayReference(e.target.value)}
                    maxLength={100}
                    placeholder="Txn / receipt no."
                    disabled={payPosting || submitting}
                  />
                </div>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCollectPayment}
                disabled={payPosting || submitting || !payAmount.trim()}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem' }}
              >
                {payPosting ? 'Recording…' : 'Record payment'}
              </button>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>
                Recorded immediately, before check-in completes.
              </div>
            </div>
          )}

          {/* ── FO → booking staff request lane (Phase B). Request-only: the
                 desk describes what it needs changed (rate, billing, terms) and
                 booking staff resolve or decline it in the Approval Inbox. The
                 thread below closes the loop — the response arrives HERE, where
                 the request was made, not in someone's inbox. ── */}
          <div style={{ marginTop: '0.875rem' }}>
            {changeRequests.map((cr) => {
              const decided = cr.approvalStatusCode === 'APPROVED' || cr.approvalStatusCode === 'REJECTED';
              const pill = cr.approvalStatusCode === 'APPROVED'
                ? { text: 'RESOLVED', bg: 'rgba(16,185,129,.12)', fg: '#047857', bd: 'rgba(16,185,129,.35)' }
                : cr.approvalStatusCode === 'REJECTED'
                  ? { text: 'DECLINED', bg: 'rgba(185,28,28,.1)', fg: '#b91c1c', bd: 'rgba(185,28,28,.3)' }
                  : { text: 'OPEN', bg: 'rgba(245,158,11,.12)', fg: '#b45309', bd: 'rgba(245,158,11,.4)' };
              return (
                <div
                  key={cr.approvalRequestId}
                  style={{
                    border: '1px solid rgba(109,40,217,.3)', background: 'rgba(109,40,217,.06)',
                    borderRadius: '7px', padding: '0.5rem 0.7rem', marginBottom: '0.5rem',
                    fontSize: '0.78rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 700, color: '#6d28d9', fontSize: '0.7rem' }}>
                      {cr.requestedByName || 'Front office'} · {cr.requestedAt?.slice(0, 16).replace('T', ' ')}
                    </span>
                    <span style={{
                      fontSize: '0.62rem', fontWeight: 700, padding: '0.05rem 0.5rem',
                      borderRadius: '999px', background: pill.bg, color: pill.fg,
                      border: `1px solid ${pill.bd}`, whiteSpace: 'nowrap',
                    }}>
                      {pill.text}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-primary)', margin: '0.2rem 0' }}>{cr.justification}</div>
                  {cr.requestedAmount != null && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                      Asked for ₹{Number(cr.requestedAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })} {requestedBasisLabel(cr.requestedBasis)}
                    </div>
                  )}
                  {decided && (
                    <div style={{
                      borderTop: '1px dashed rgba(109,40,217,.3)', paddingTop: '0.35rem',
                      marginTop: '0.35rem',
                    }}>
                      <span style={{ fontWeight: 700, fontSize: '0.7rem', color: pill.fg }}>
                        {cr.approvedByName || 'Booking staff'} · {cr.approvedAt?.slice(0, 16).replace('T', ' ')}
                      </span>
                      {cr.resolution === 'REPRICED' && cr.resolvedTotal != null && (
                        <div style={{ fontWeight: 600, color: '#047857' }}>
                          Re-priced — total now ₹{Number(cr.resolvedTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </div>
                      )}
                      {cr.resolution === 'NO_CHANGE' && (
                        <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Closed — no price change</div>
                      )}
                      <div style={{ color: 'var(--text-secondary)' }}>
                        {cr.approvalNotes || (cr.approvalStatusCode === 'APPROVED' ? 'Resolved.' : 'Declined.')}
                      </div>
                    </div>
                  )}
                  {!decided && (
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Awaiting booking staff…</div>
                  )}
                </div>
              );
            })}

            <div style={{
              border: '1px dashed rgba(109,40,217,.4)', borderRadius: '7px',
              padding: '0.55rem 0.7rem', background: 'var(--bg-primary)',
            }}>
              <div style={{
                fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '0.05em', color: '#6d28d9', marginBottom: '0.3rem',
              }}>
                Request a change from booking staff (nothing is applied)
              </div>
              <textarea
                className="form-input"
                rows={2}
                maxLength={500}
                value={requestText}
                onChange={(e) => setRequestText(e.target.value)}
                placeholder="Describe the change you need — rate, billing, booking terms…"
                disabled={requestSending || submitting}
                style={{ fontSize: '0.8125rem', resize: 'vertical' }}
              />
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>If it is a price — amount asked:</span>
                <input
                  type="number"
                  className="form-input"
                  min={0}
                  step={100}
                  value={requestAmount}
                  onChange={(e) => setRequestAmount(e.target.value)}
                  placeholder="Optional"
                  disabled={requestSending || submitting}
                  style={{ width: '8rem', fontSize: '0.8125rem' }}
                />
                <select
                  className="form-input"
                  value={requestBasis}
                  onChange={(e) => setRequestBasis(e.target.value as 'PER_NIGHT' | 'STAY_TOTAL')}
                  disabled={requestSending || submitting || requestAmount.trim() === ''}
                  style={{ width: 'auto', fontSize: '0.8125rem' }}
                >
                  <option value="STAY_TOTAL">Stay total</option>
                  <option value="PER_NIGHT">Rate per night</option>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.4rem' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendRequest}
                  disabled={requestSending || submitting || !requestText.trim()}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.8rem' }}
                >
                  {requestSending ? 'Sending…' : 'Submit request'}
                </button>
              </div>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <SectionedModalShell
        title={<>{isAmend ? 'Edit Check-In' : auditCorrection ? 'Record missed check-in' : 'Check In'} — {booking.bookingNumber}</>}
        headerExtra={
          /* GRC number — generated server-side unless the operator overrides it
             for a pre-printed card. Shown in the header because it identifies
             the physical document the guest signs. Amend mode: read-only — an
             amendment corrects the card, it does not reissue it. */
          <span className="chk-grc" title="Guest Registration Card number">
            GRC{' '}
            {isAmend ? (
              <b>{grcNumber.trim() || '—'}</b>
            ) : grcNumberEditing ? (
              <input
                type="text"
                className="form-input"
                autoFocus
                value={grcNumber}
                onChange={(e) => setGrcNumber(e.target.value)}
                onBlur={() => setGrcNumberEditing(false)}
                maxLength={30}
                placeholder="Card number"
                disabled={submitting}
                style={{ width: '11rem', padding: '0.15rem 0.35rem', fontSize: '0.75rem' }}
              />
            ) : (
              <>
                <b>{grcNumber.trim() || 'auto-generated'}</b>
                <button
                  type="button"
                  onClick={() => setGrcNumberEditing(true)}
                  disabled={submitting}
                  title="Enter the number from a pre-printed GRC book"
                  style={{
                    marginLeft: '0.3rem', border: 'none', background: 'transparent',
                    color: 'var(--primary)', cursor: 'pointer', fontWeight: 700, padding: 0,
                  }}
                >
                  ✎
                </button>
              </>
            )}
          </span>
        }
        summary={
          <>
            <strong style={{ color: 'var(--text-primary)', fontSize: '0.875rem' }}>
              {bk.guestFullName || '—'}
            </strong>
            <span style={{ color: 'var(--text-secondary)' }}>{bk.propertyName}</span>
            <span style={{ color: 'var(--text-primary)' }}>
              <strong>{bk.totalRooms}</strong> room{bk.totalRooms !== 1 ? 's' : ''}
              {' · '}
              <strong>{bk.numNights}</strong> night{bk.numNights !== 1 ? 's' : ''}
              {' · '}
              <strong>{bk.numAdults}</strong>A{(bk.numChildren ?? 0) > 0 ? <> + <strong>{bk.numChildren}</strong>C</> : null}
            </span>
            <span style={{ color: 'var(--text-secondary)' }}>
              Planned {bk.checkInDate?.slice(0, 10) ?? '—'} → {bk.checkOutDate?.slice(0, 10) ?? '—'}
            </span>
            {/* Meal-plan chips (decision 7) — distinct plan codes on the stay. */}
            {mealPlanChips.map((code) => (
              <ShellChip key={code} tone="info">{code}</ShellChip>
            ))}
            {/* Addon summary (decision 7) — names × qty, compact. */}
            {bookingAddons.length > 0 && (
              <span style={{ color: 'var(--text-secondary)' }}>
                Addons: {bookingAddons.map((a) => `${a.addonName}${a.quantity > 1 ? ` × ${a.quantity}` : ''}`).join(', ')}
              </span>
            )}
            {/* Live status chips — visible whatever section is open, so the
                operator never has to tour sections to answer "can I press
                Check In?". */}
            <ShellChip tone={balanceAmt > 0 ? 'warn' : 'ok'} title="Total collected vs payable">
              {balanceAmt > 0 ? `Balance ${fmtAmt(balanceAmt)}` : 'Paid ✓'}
            </ShellChip>
            <ShellChip
              tone={assignments.length > 0 && assignedCount === assignments.length ? 'ok' : 'warn'}
              title="Rooms assigned"
            >
              Rooms {assignedCount}/{assignments.length}
            </ShellChip>
            <ShellChip tone="info" title="Check-in date · time">
              {checkInDate} · {checkInTime}
            </ShellChip>
            {/* Who pays (slice 3b) — the desk confirms it at arrival. */}
            <ShellChip
              tone="info"
              title="Settlement — who pays for this stay. Charges posted from check-in follow it."
            >
              {settlementModeLabel(bk.settlementMode || 'GUEST')}
              {(bk.settlementMode || 'GUEST') !== 'GUEST' && (bk.agentName || bk.corporateName || bk.otaDisplayName || bk.otaName)
                ? ` · ${bk.agentName || bk.corporateName || bk.otaDisplayName || bk.otaName}` : ''}
              {canChangeSettlement && (
                <button
                  type="button"
                  onClick={() => setSettlementOpen(true)}
                  style={{
                    marginLeft: '0.4rem', background: 'transparent', border: 'none', padding: 0,
                    color: 'var(--primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline',
                  }}
                >
                  Change
                </button>
              )}
            </ShellChip>
            {/* Registration card completed after check-in (GRC-3). */}
            {!isAmend && !auditCorrection && grcLater && (
              <ShellChip tone="info" title="This property completes the registration card after check-in: only the primary guest and mobile are needed now">
                Card: complete later
              </ShellChip>
            )}
            {isAmend && amendRecord?.grc?.completeLater && (() => {
              const g = amendRecord.grc;
              const formC = formCText(g.formCDueAt);
              return g.pending ? (
                <ShellChip tone="warn" title="Still to be keyed in from the guest's card">
                  GRC pending: {g.missing.join(', ')}{formC ? ` · ${formC.text}` : ''}
                </ShellChip>
              ) : (
                <ShellChip tone="ok" title="Everything the property needs on the card is recorded">GRC complete ✓</ShellChip>
              );
            })()}
            {/* Room charging (BR-FOL-008) — group rooms shared by several guests
                usually go "no room charge": outlets collect directly. */}
            {!auditCorrection && canControlRoomCharging() && (!isAmend || chargingFolio) && (() => {
              const off = isAmend ? !!chargingFolio?.noPost : !!pendingCharging?.noPost;
              return (
                <ShellChip
                  tone={off ? 'warn' : 'info'}
                  title="Room charging — off means the restaurant and other outlets collect directly; desk charges and room nights still post"
                >
                  {off ? 'No room charge' : 'Room charging on'}
                  <button
                    type="button"
                    onClick={() => setChargingOpen(true)}
                    style={{
                      marginLeft: '0.4rem', background: 'transparent', border: 'none', padding: 0,
                      color: 'var(--primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline',
                    }}
                  >
                    Change
                  </button>
                </ShellChip>
              );
            })()}
          </>
        }
        banners={(specialNotes || notConfirmed) ? (
          <>
            {/* ── Special notes — loud banner; the thing desks miss (decision 7).
                   Full width and above every section so no section hides it. ── */}
            {specialNotes && (
              <div style={{
                padding: '0.5rem 0.75rem',
                background: 'rgba(245, 158, 11, 0.12)',
                border: '1px solid rgba(245, 158, 11, 0.45)',
                borderLeft: '4px solid #d97706',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                marginBottom: '0.75rem',
                color: 'var(--text-primary)',
              }}>
                <strong>Special notes:</strong> {specialNotes}
              </div>
            )}

            {/* ── Status guard — a Modify Stay edit can send the booking for
                   approval; check-in waits until it's CONFIRMED again. ── */}
            {notConfirmed && (
              <div style={{
                padding: '0.5rem 0.75rem',
                background: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid rgba(220, 38, 38, 0.35)',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                marginBottom: '0.75rem',
                color: 'var(--text-primary)',
              }}>
                This booking is now <strong>{bk.bookingStatus ?? bk.bookingStatusCode}</strong> — the stay
                change was sent for approval. Check-in becomes available once it is approved.
              </div>
            )}
          </>
        ) : undefined}
        sections={sections}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        onClose={handleClose}
        onMinimize={tabId ? handleMinimize : undefined}
        busy={submitting}
        loading={{
          active: submitting || loadingBooking,
          label: submitting ? (isAmend ? 'Saving…' : 'Checking in…') : 'Loading booking…',
        }}
        footer={
          <>
            {/* Say what is blocking instead of making the operator press the
                button to find out — each entry jumps to its section. */}
            <ShellBlockers blockers={blockerItems} onJump={setActiveSection} />
            {/* The registration card (GRC-2): printed after a check-in, or again from Edit Check-In. */}
            {!isAmend && !auditCorrection && (
              <label htmlFor="chk-print-card" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', marginRight: 'auto' }}
                title="Opens the pre-filled card for the guest to complete and sign">
                <input id="chk-print-card" type="checkbox" checked={printCardAfter} disabled={submitting}
                  onChange={(e) => changePrintCardAfter(e.target.checked)} />
                Print registration card
              </label>
            )}
            {isAmend && (
              <button className="btn btn-secondary" disabled={submitting} style={{ marginRight: 'auto' }}
                onClick={() => { void openPdf(() => outputProfileService.grcPdf(booking.bookingId), 'The registration card did not print.'); }}>
                <IconSystem.print size={16} /> Print card
              </button>
            )}
            <button className="btn btn-secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={submitting || loadingBooking || notConfirmed || blockers.length > 0}
              title={
                notConfirmed ? 'Booking is awaiting approval — check-in is unavailable'
                  : blockers.length > 0 ? blockers.join(' · ')
                  : undefined
              }
            >
              <IconSystem.check size={16} />
              {/* The button IS the summary — count + time — so the no-warnings
                  fast path can commit without a separate confirm dialog. */}
              {isAmend
                ? 'Save Changes'
                : assignments.length > 0
                  ? `${auditCorrection ? 'Record check-in' : 'Check In'} · ${assignments.length} room${assignments.length !== 1 ? 's' : ''} · ${checkInTime}`
                  : auditCorrection ? 'Record check-in' : 'Check In'}
            </button>
          </>
        }
      />

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Check-In"
        message={
          `Check in booking "${booking.bookingNumber}" with the selected rooms?`
          // Downgrades get their own sentence: with every category one tap away
          // an accidental one is easy, and the guest would otherwise silently pay
          // the higher booked rate for a lesser room.
          + (() => {
            const downs = categoryChanges.filter(
              (a) => classifyChange(a.bookedCategoryId, a.roomCategoryId) === 'downgrade',
            );
            if (downs.length === 0) return '';
            return ' ⚠ DOWNGRADE — ' + downs
              .map((a) => {
                const room = a.roomId ? roomInfoRef.current[a.roomId]?.roomNumber : undefined;
                return `${room ? `Room ${room}: ` : ''}booked ${a.bookedCategoryName}, allocated ${a.categoryName}`;
              })
              .join('; ')
              + '. The guest still pays the booked rate — adjust it if that is not intended.';
          })()
          + (categoryChanges.length > 0
            ? ' CATEGORY CHANGES: ' + categoryChanges
                .map((a) => {
                  const room = a.roomId ? roomInfoRef.current[a.roomId]?.roomNumber : undefined;
                  return `${room ? `Room ${room}: ` : ''}${a.bookedCategoryName} → ${a.categoryName}`;
                })
                .join('; ')
            + '. The guest continues to pay the booked-category rate; inventory and reports reconcile automatically.'
            : '')
          + (dirtyAssignedRooms.length > 0
            ? ` NOTE: room${dirtyAssignedRooms.length !== 1 ? 's' : ''} ${dirtyAssignedRooms.map((r) => r.roomNumber).join(', ')} still marked Dirty.`
            : '')
          + (unassignedRooms > 0
            ? ` NO-SHOW: ${unassignedRooms} unassigned room${unassignedRooms !== 1 ? 's' : ''} will be marked no-show — remaining nights go back on sale; the charge is decided by booking staff per the no-show policy.`
            : '')
          // Projected shortage (BR-SHORT-002). Last in the summary and phrased as
          // a commitment rather than a blocker: the arrival night is covered, the
          // guest is checking in either way, and what the desk is confirming is
          // that the property will manage the later nights.
          + (shortfalls.length > 0
            ? ' ⚠ ROOM SHORTAGE — ' + shortfalls.map(shortfallMessage).join(' ')
            + ' Check-in still proceeds — the assigned rooms are physically free — but confirming'
            + ' commits them through the stay and leaves fewer ways to solve the shortage'
            + ' (upgrade, relocate, consolidate or walk).'
            : '')
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* The nested Edit Booking flow was REMOVED (FO process review, 2026-08):
          most front-office users hold no booking-edit permission, and editing
          the reservation from the desk is the wrong process. Pax, meal plan and
          the agreed rate are now edited in place via the Arrival changes panel
          in the Arrival & Stay section, under its own 'arrival-changes'
          permission. Anything outside that narrow set (dates, property,
          restructuring) belongs to Reservations, or to Extend Stay once the
          guest is in house. */}

      {/* Settlement change from the chip (slice 3b) — only the mode is taken
          back into the check-in, so nothing else it holds is reloaded. */}
      <ChangeSettlementModal
        isOpen={settlementOpen}
        booking={bk}
        onClose={() => setSettlementOpen(false)}
        onChanged={(updated) => {
          if (updated?.settlementMode) {
            setLiveBooking(prev => ({ ...(prev ?? booking), settlementMode: updated.settlementMode }));
          }
        }}
      />

      <RoomChargingModal
        isOpen={chargingOpen}
        label={isAmend ? (chargingFolio?.folioNumber ?? '') : (booking?.bookingNumber ?? '')}
        folioId={isAmend ? chargingFolio?.folioId : null}
        noPost={isAmend ? !!chargingFolio?.noPost : !!pendingCharging?.noPost}
        creditLimit={isAmend ? chargingFolio?.creditLimit : pendingCharging?.creditLimit}
        initialReason={isAmend ? undefined : pendingCharging?.reason}
        onClose={() => setChargingOpen(false)}
        onSaved={(updated) => { setChargingFolio(updated); setChargingOpen(false); }}
        onPending={(request) => {
          // Back to "on" with no limit = nothing to apply after check-in.
          setPendingCharging(request.noPost || request.creditLimit != null ? request : null);
          setChargingOpen(false);
        }}
      />
    </>
  );
};

export type { QuickCheckInModalProps };
export default QuickCheckInModal;
